import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../hooks/useAuth';
import {
  AlertCircle,
  CheckCircle,
  Download,
  Server,
  Database,
  Brain,
  Shield,
  Cloud,
  ChevronRight,
  Copy,
  RefreshCw,
  Loader2,
  Package,
  FileCode,
  Lock,
  Zap,
  Key,
  Info,
  GitBranch,
  Network,
  ExternalLink,
} from 'lucide-react';
import {
  apiClient,
  OnPremRelease,
  OnPremStatus,
  SupportedLLMModel,
  GenerateHelmValuesResponse,
} from '../api/client';

// Cloud provider icons
import { ReactComponent as AWSIcon } from '../assets/icons/aws.svg';
import { ReactComponent as AWSIconDark } from '../assets/icons/aws-dark.svg';
import { ReactComponent as GCPIcon } from '../assets/icons/gcp.svg';
import { ReactComponent as AzureIcon } from '../assets/icons/azure.svg';

// Hook to detect dark mode
function useDarkMode() {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkDarkMode = () => {
      const isDarkMode = document.documentElement.classList.contains('dark');
      setIsDark(isDarkMode);
    };

    checkDarkMode();

    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  return isDark;
}

// AWS Icon component that switches based on theme
function AWSIconThemed({ className }: { className?: string }) {
  const isDark = useDarkMode();
  return isDark ? <AWSIconDark className={className} /> : <AWSIcon className={className} />;
}

// Wizard Steps
const WIZARD_STEPS = [
  { id: 'overview', title: 'Overview', icon: Server },
  { id: 'deployment-type', title: 'Deployment Type', icon: Cloud },
  { id: 'llm-config', title: 'LLM Configuration', icon: Brain },
  { id: 'infrastructure', title: 'Infrastructure', icon: Database },
  { id: 'github', title: 'GitHub Integration', icon: GitBranch },
  { id: 'generate', title: 'Generate Config', icon: FileCode },
  { id: 'download', title: 'Download & Deploy', icon: Download },
];

const OnPremDeploymentPage: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  
  // API data
  const [status, setStatus] = useState<OnPremStatus | null>(null);
  const [, setReleases] = useState<OnPremRelease[]>([]);
  const [llmModels, setLlmModels] = useState<SupportedLLMModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form state
  const [cloudProvider, setCloudProvider] = useState<'aws' | 'gcp' | 'azure'>('aws');
  const [deploymentType, setDeploymentType] = useState<'standard' | 'airgapped'>('standard');
  const [llmProvider, setLlmProvider] = useState('bedrock');
  const [llmModelId, setLlmModelId] = useState('us.anthropic.claude-sonnet-4-5-20250929-v1:0');
  const [bedrockEndpointUrl, setBedrockEndpointUrl] = useState('');
  const [bedrockRegion, setBedrockRegion] = useState('us-east-1');
  const [openaiApiKey, setOpenaiApiKey] = useState('');
  const [openaiModelId, setOpenaiModelId] = useState('gpt-5.2-2025-12-11');
  const [secretsProvider, setSecretsProvider] = useState('aws');
  
  // Infrastructure config
  const [ecrRegistryUrl, setEcrRegistryUrl] = useState('');
  const [useExternalDatabase, setUseExternalDatabase] = useState(false);
  const [postgresHost, setPostgresHost] = useState('');
  const [postgresPort, setPostgresPort] = useState(5432);
  const [postgresDb, setPostgresDb] = useState('kestrel');
  const [postgresUser, setPostgresUser] = useState('kestrel');
  const [redisHost, setRedisHost] = useState('');
  const [redisPort, setRedisPort] = useState(6379);
  const [iamRoleArn, setIamRoleArn] = useState('');
  const [ingressClassName, setIngressClassName] = useState('nginx');
  const [ingressHost, setIngressHost] = useState('');
  
  // Elasticsearch/OpenSearch config (optional)
  const [elasticsearchEnabled, setElasticsearchEnabled] = useState(false);
  const [elasticsearchEndpoint, setElasticsearchEndpoint] = useState('');
  const [elasticsearchUsername, setElasticsearchUsername] = useState('');
  const [elasticsearchPassword, setElasticsearchPassword] = useState('');
  const [showElasticsearchInstructions, setShowElasticsearchInstructions] = useState(false);
  
  // GitHub Enterprise Server config (for air-gapped deployments)
  const [githubEnterpriseUrl, setGithubEnterpriseUrl] = useState('');
  const [githubAppId, setGithubAppId] = useState('');
  const [githubAppSlug, setGithubAppSlug] = useState('kestrel');
  
  // Generated values
  const [generatedValues, setGeneratedValues] = useState<GenerateHelmValuesResponse | null>(null);
  const [generating, setGenerating] = useState(false);
  
  // TLS Certificate for GitHub Enterprise (generated on-demand)
  const [tlsCACert, setTlsCACert] = useState<string>('');
  const [generatingTLSCert, setGeneratingTLSCert] = useState(false);
  
  // License key
  const [licenseKey, setLicenseKey] = useState<string>('');
  
  // Credentials modal
  const [credentialsModalOpen, setCredentialsModalOpen] = useState(false);
  const [credentialsData, setCredentialsData] = useState<{
    registry: string;
    username: string;
    token: string;
    docker_login_command: string;
    k8s_secret_command: string;
    commands: string;
  } | null>(null);

  // Cloud-specific configuration labels (using bedrockRegion for AWS placeholders)
  const cloudConfig = {
    aws: {
      name: 'Amazon Web Services (AWS)',
      shortName: 'AWS',
      registryName: 'Amazon ECR',
      registryPlaceholder: `123456789012.dkr.ecr.${bedrockRegion}.amazonaws.com`,
      llmService: 'Amazon Bedrock',
      secretsService: 'AWS Secrets Manager',
      iamLabel: 'IAM Role ARN',
      iamPlaceholder: 'arn:aws:iam::123456789012:role/kestrel-role',
    },
    gcp: {
      name: 'Google Cloud Platform (GCP)',
      shortName: 'GCP',
      registryName: 'Artifact Registry',
      registryPlaceholder: 'us-docker.pkg.dev/project-id/repo',
      llmService: 'Vertex AI',
      secretsService: 'Secret Manager',
      iamLabel: 'Service Account',
      iamPlaceholder: 'kestrel@project-id.iam.gserviceaccount.com',
    },
    azure: {
      name: 'Microsoft Azure',
      shortName: 'Azure',
      registryName: 'Azure Container Registry',
      registryPlaceholder: 'kestrel.azurecr.io',
      llmService: 'Azure OpenAI',
      secretsService: 'Key Vault',
      iamLabel: 'Managed Identity',
      iamPlaceholder: '/subscriptions/.../resourceGroups/.../providers/Microsoft.ManagedIdentity/...',
    },
  };
  
  const currentCloud = cloudConfig[cloudProvider];

  // Check if user has on-prem access
  useEffect(() => {
    if (!authLoading && user && !user.is_onprem_enabled) {
      navigate('/home');
    }
  }, [authLoading, user, navigate]);

  // Load data on mount
  useEffect(() => {
    if (user?.is_onprem_enabled) {
      loadData();
    }
  }, [user]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Don't render if user doesn't have on-prem access
  if (!user?.is_onprem_enabled) {
    return null;
  }

  const loadData = async () => {
    setLoading(true);
    try {
      const [statusData, releasesData, modelsData, licenseData] = await Promise.all([
        apiClient.getOnPremStatus().catch(() => null),
        apiClient.getOnPremReleases().catch(() => []),
        apiClient.getSupportedLLMModels().catch(() => []),
        apiClient.getCurrentLicense(true).catch(() => null),
      ]);
      
      // Set license key if available
      if (licenseData?.license_key) {
        setLicenseKey(licenseData.license_key);
      }

      if (statusData) {
        setStatus(statusData);
        if (statusData.deployment) {
          // Pre-fill form from existing deployment
          setDeploymentType(statusData.deployment.deployment_type || 'standard');
          setLlmProvider(statusData.deployment.llm_provider || 'bedrock');
          setLlmModelId(statusData.deployment.llm_model_id || 'us.anthropic.claude-sonnet-4-5-20250929-v1:0');
          setBedrockEndpointUrl(statusData.deployment.llm_endpoint_url || '');
          setBedrockRegion(statusData.deployment.bedrock_region || 'us-east-1');
          setSecretsProvider(statusData.deployment.secrets_provider || 'aws');
          
          // Restore infrastructure configuration from deployment_config
          const config = statusData.deployment.deployment_config;
          if (config) {
            if (config.ecr_registry_url) setEcrRegistryUrl(config.ecr_registry_url);
            if (config.iam_role_arn) setIamRoleArn(config.iam_role_arn);
            if (config.use_external_database !== undefined) setUseExternalDatabase(config.use_external_database);
            if (config.postgres_host) setPostgresHost(config.postgres_host);
            if (config.postgres_port) setPostgresPort(config.postgres_port);
            if (config.postgres_db) setPostgresDb(config.postgres_db);
            if (config.postgres_user) setPostgresUser(config.postgres_user);
            if (config.redis_host) setRedisHost(config.redis_host);
            if (config.redis_port) setRedisPort(config.redis_port);
            if (config.elasticsearch_enabled !== undefined) setElasticsearchEnabled(config.elasticsearch_enabled);
            if (config.elasticsearch_endpoint) setElasticsearchEndpoint(config.elasticsearch_endpoint);
            if (config.elasticsearch_username) setElasticsearchUsername(config.elasticsearch_username);
            if (config.ingress_class_name) setIngressClassName(config.ingress_class_name);
            if (config.ingress_host) setIngressHost(config.ingress_host);
            // GitHub Enterprise Server (for air-gapped deployments)
            if (config.github_enterprise_url) setGithubEnterpriseUrl(config.github_enterprise_url);
            if (config.github_app_id) setGithubAppId(config.github_app_id);
            if (config.github_app_slug) setGithubAppSlug(config.github_app_slug);
          }
        }
      }

      setReleases(releasesData);
      setLlmModels(modelsData);
    } catch (err) {
      setError('Failed to load deployment information');
    } finally {
      setLoading(false);
    }
  };

  const handleStepComplete = (step: number) => {
    setCompletedSteps(prev => {
      const newSet = new Set(Array.from(prev));
      newSet.add(step);
      return newSet;
    });
    if (step < WIZARD_STEPS.length - 1) {
      setCurrentStep(step + 1);
    }
  };

  const handleSaveDeploymentConfig = async (includeInfrastructure = false) => {
    try {
      const request: any = {
        deployment_type: deploymentType,
        llm_provider: llmProvider,
        llm_model_id: llmModelId,
        llm_endpoint_url: bedrockEndpointUrl,
        bedrock_region: bedrockRegion,
        openai_api_key: llmProvider === 'openai' ? openaiApiKey : '',
        openai_model_id: llmProvider === 'openai' ? openaiModelId : '',
        secrets_provider: secretsProvider,
      };
      
      // Include infrastructure configuration if specified
      if (includeInfrastructure) {
        request.deployment_config = {
          ecr_registry_url: ecrRegistryUrl,
          iam_role_arn: iamRoleArn,
          use_external_database: useExternalDatabase,
          postgres_host: useExternalDatabase ? postgresHost : '',
          postgres_port: useExternalDatabase ? postgresPort : 5432,
          postgres_db: useExternalDatabase ? postgresDb : 'kestrel',
          postgres_user: useExternalDatabase ? postgresUser : 'kestrel',
          redis_host: useExternalDatabase ? redisHost : '',
          redis_port: useExternalDatabase ? redisPort : 6379,
          elasticsearch_enabled: elasticsearchEnabled,
          elasticsearch_endpoint: elasticsearchEnabled ? elasticsearchEndpoint : '',
          elasticsearch_username: elasticsearchEnabled ? elasticsearchUsername : '',
          // Note: password not saved for security
          ingress_class_name: ingressClassName,
          ingress_host: ingressHost,
          // GitHub Enterprise Server (for air-gapped deployments)
          github_enterprise_url: deploymentType === 'airgapped' ? githubEnterpriseUrl : '',
          github_app_id: deploymentType === 'airgapped' ? githubAppId : '',
          github_app_slug: deploymentType === 'airgapped' ? githubAppSlug : '',
        };
      }
      
      await apiClient.createOrUpdateOnPremDeployment(request);
      
      setSuccess('Configuration saved successfully');
      await loadData();
    } catch (err) {
      setError('Failed to save configuration');
    }
  };

  const handleGenerateValues = async () => {
    setGenerating(true);
    setError('');
    try {
      const values = await apiClient.generateHelmValues({
        ecr_registry_url: ecrRegistryUrl,
        deployment_type: deploymentType,
        use_external_database: useExternalDatabase,
        postgres_host: useExternalDatabase ? postgresHost : '',
        postgres_port: useExternalDatabase ? postgresPort : 5432,
        postgres_db: useExternalDatabase ? postgresDb : 'kestrel',
        postgres_user: useExternalDatabase ? postgresUser : 'kestrel',
        redis_host: useExternalDatabase ? redisHost : '',
        redis_port: useExternalDatabase ? redisPort : 6379,
        llm_provider: llmProvider,
        llm_model_id: llmModelId,
        llm_endpoint_url: bedrockEndpointUrl,
        bedrock_region: bedrockRegion,
        openai_api_key: llmProvider === 'openai' ? openaiApiKey : '',
        openai_model_id: llmProvider === 'openai' ? openaiModelId : '',
        iam_role_arn: iamRoleArn,
        secrets_provider: secretsProvider,
        ingress_class_name: ingressClassName,
        ingress_host: ingressHost,
        // Elasticsearch/OpenSearch configuration
        elasticsearch_enabled: elasticsearchEnabled,
        elasticsearch_endpoint: elasticsearchEnabled ? elasticsearchEndpoint : '',
        elasticsearch_username: elasticsearchEnabled ? elasticsearchUsername : '',
        elasticsearch_password: elasticsearchEnabled ? elasticsearchPassword : '',
        // GitHub Enterprise Server configuration (for air-gapped deployments)
        github_enterprise_url: deploymentType === 'airgapped' ? githubEnterpriseUrl : '',
        github_app_id: deploymentType === 'airgapped' ? githubAppId : '',
        github_app_slug: deploymentType === 'airgapped' ? githubAppSlug : '',
        // License key for on-prem activation
        license_key: licenseKey,
      });
      
      setGeneratedValues(values);
      setSuccess('Helm values generated successfully');
    } catch (err) {
      setError('Failed to generate helm values');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    // Record download
    if (status?.latest_release?.version) {
      apiClient.recordOnPremDownload({
        release_version: status.latest_release.version,
        component: filename.replace('.yaml', ''),
      }).catch(console.error);
    }
  };

  // Generate TLS certificate for GitHub Enterprise integration
  const handleGenerateAndDownloadTLSCert = async () => {
    if (!ingressHost) {
      toast.error('Please configure the Ingress Hostname in the Infrastructure step first');
      return;
    }

    setGeneratingTLSCert(true);
    try {
      const response = await apiClient.generateTLSCert(ingressHost);
      setTlsCACert(response.ca_cert);
      
      // Trigger download
      const blob = new Blob([response.ca_cert], { type: 'application/x-pem-file' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'kestrel-ca.crt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('CA certificate generated and downloaded');
    } catch (err) {
      console.error('Failed to generate TLS certificate:', err);
      toast.error('Failed to generate TLS certificate');
    } finally {
      setGeneratingTLSCert(false);
    }
  };

  const handleCopyToClipboard = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setSuccess('Copied to clipboard');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError('Failed to copy to clipboard');
    }
  };

  const renderStepIndicator = () => (
    <div className="mb-8">
      <div className="flex justify-between items-center">
        {WIZARD_STEPS.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === currentStep;
          const isCompleted = completedSteps.has(index);
          
          return (
            <React.Fragment key={step.id}>
              <div 
                className={`flex flex-col items-center cursor-pointer ${
                  isActive ? 'text-foreground' : isCompleted ? 'text-green-600' : 'text-gray-400'
                }`}
                onClick={() => (isCompleted || index <= currentStep) && setCurrentStep(index)}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                  isActive ? 'bg-accent border-2 border-foreground' :
                  isCompleted ? 'bg-green-100 border-2 border-green-600' :
                  'bg-gray-100 border-2 border-gray-300'
                }`}>
                  {isCompleted ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <span className="text-xs font-medium text-center">{step.title}</span>
              </div>
              {index < WIZARD_STEPS.length - 1 && (
                <div className={`flex-1 h-1 mx-2 ${
                  completedSteps.has(index) ? 'bg-green-500' : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      <Progress value={(completedSteps.size / WIZARD_STEPS.length) * 100} className="mt-4" />
    </div>
  );

  const renderOverviewStep = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Server className="w-5 h-5" />
          On-Premises Deployment
        </CardTitle>
        <CardDescription>
          Deploy Kestrel in your own cloud environment for complete control and data sovereignty.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Cloud Provider Selection */}
        <div className="space-y-2">
          <Label>Select Cloud Provider</Label>
          <Select value={cloudProvider} onValueChange={(value: 'aws' | 'gcp' | 'azure') => setCloudProvider(value)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="aws">
                <div className="flex items-center gap-2">
                  <AWSIconThemed className="w-4 h-4" />
                  Amazon Web Services (AWS)
                </div>
              </SelectItem>
              <SelectItem value="gcp" disabled>
                <div className="flex items-center gap-2">
                  <GCPIcon className="w-4 h-4" />
                  Google Cloud Platform (Coming Soon)
                </div>
              </SelectItem>
              <SelectItem value="azure" disabled>
                <div className="flex items-center gap-2">
                  <AzureIcon className="w-4 h-4" />
                  Microsoft Azure (Coming Soon)
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {status?.latest_release && (
          <Alert>
            <Package className="h-4 w-4" />
            <AlertTitle>Latest Release: {status.latest_release.version}</AlertTitle>
            <AlertDescription>
              <div className="flex flex-col gap-1">
                <div>
                  <span className="text-muted-foreground">Helm Chart:</span> {status.latest_release.version}
                  {' • '}
                  <span className="text-muted-foreground">Images:</span> {status.latest_release.image_version || 'v1.0.0'}
                </div>
                <div>
                  Released on {new Date(status.latest_release.release_date).toLocaleDateString()}
                  {status.upgrade_available && (
                    <Badge variant="destructive" className="ml-2">Upgrade Available</Badge>
                  )}
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {status?.deployment && (
          <div className="p-4 bg-muted rounded-lg">
            <h4 className="font-medium mb-2">Current Deployment</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Version:</span>
                <span className="ml-2 font-medium">{status.deployment.current_version || 'Not deployed'}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Type:</span>
                <span className="ml-2 font-medium capitalize">{status.deployment.deployment_type}</span>
              </div>
              <div>
                <span className="text-muted-foreground">LLM Provider:</span>
                <span className="ml-2 font-medium capitalize">{status.deployment.llm_provider}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Status:</span>
                <span className="ml-2 font-medium capitalize">{status.deployment.status}</span>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Card className="border-2">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-4">
                <Cloud className="w-8 h-8 text-muted-foreground" />
                <div>
                  <h4 className="font-semibold">Standard Deployment</h4>
                  <p className="text-sm text-muted-foreground">Internet access available</p>
                </div>
              </div>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• Pull images directly from container registry</li>
                <li>• {currentCloud.llmService} via regional endpoint</li>
                <li>• Outbound internet access required</li>
              </ul>
            </CardContent>
          </Card>
          
          <Card className="border-2">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-4">
                <Lock className="w-8 h-8 text-muted-foreground" />
                <div>
                  <h4 className="font-semibold">Air-Gapped Deployment</h4>
                  <p className="text-sm text-muted-foreground">No internet access</p>
                </div>
              </div>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• Import images from downloaded tarball</li>
                <li>• {currentCloud.llmService} via VPC endpoint</li>
                <li>• No outbound internet connectivity</li>
              </ul>
            </CardContent>
          </Card>
        </div>

        <Button onClick={() => handleStepComplete(0)} className="w-full">
          Get Started <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );

  const renderDeploymentTypeStep = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Cloud className="w-5 h-5" />
          Select Deployment Type
        </CardTitle>
        <CardDescription>
          Choose your deployment environment based on network access requirements.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div 
            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
              deploymentType === 'standard' 
                ? 'border-foreground bg-accent' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => setDeploymentType('standard')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Cloud className="w-6 h-6 text-muted-foreground" />
                <div>
                  <h4 className="font-semibold">Standard Deployment</h4>
                  <p className="text-sm text-muted-foreground">
                    Cluster has internet access for pulling images and API calls
                  </p>
                </div>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 ${
                deploymentType === 'standard' ? 'border-foreground bg-foreground' : 'border-gray-300'
              }`}>
                {deploymentType === 'standard' && <CheckCircle className="w-4 h-4 text-white" />}
              </div>
            </div>
          </div>

          <div 
            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
              deploymentType === 'airgapped' 
                ? 'border-foreground bg-accent' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => setDeploymentType('airgapped')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Lock className="w-6 h-6 text-muted-foreground" />
                <div>
                  <h4 className="font-semibold">Air-Gapped Deployment</h4>
                  <p className="text-sm text-muted-foreground">
                    No internet access - requires VPC endpoints and image import
                  </p>
                </div>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 ${
                deploymentType === 'airgapped' ? 'border-foreground bg-foreground' : 'border-gray-300'
              }`}>
                {deploymentType === 'airgapped' && <CheckCircle className="w-4 h-4 text-white" />}
              </div>
            </div>
          </div>
        </div>

        {deploymentType === 'airgapped' && (
          <Alert className="bg-amber-50 border-amber-200">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertTitle className="text-amber-800">Air-Gapped Requirements ({currentCloud.shortName})</AlertTitle>
            <AlertDescription className="text-amber-700">
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>VPC endpoint for {currentCloud.llmService}</li>
                <li>VPC endpoint for {currentCloud.secretsService}</li>
                <li>Internal {currentCloud.registryName} with imported images</li>
                <li>Internal PostgreSQL and Redis</li>
              </ul>
            </AlertDescription>
          </Alert>
        )}

        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCurrentStep(0)}>
            Back
          </Button>
          <Button onClick={() => handleStepComplete(1)} className="flex-1">
            Continue <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderLLMConfigStep = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Brain className="w-5 h-5" />
          LLM Configuration
        </CardTitle>
        <CardDescription>
          Configure the AI model that powers Kestrel's agentic capabilities.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block">LLM Provider</Label>
            <Select value={llmProvider} onValueChange={setLlmProvider}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bedrock">
                  <div className="flex items-center gap-2">
                    <AWSIconThemed className="w-4 h-4" />
                    Amazon Bedrock (Recommended for AWS)
                  </div>
                </SelectItem>
                <SelectItem value="openai" disabled={deploymentType === 'airgapped'}>
                  <div className="flex items-center gap-2">
                    OpenAI {deploymentType === 'airgapped' && '(Requires Internet)'}
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {llmProvider === 'bedrock' && (
            <>
              <div>
                <Label className="mb-1.5 block">Model</Label>
                <Select value={llmModelId} onValueChange={setLlmModelId}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {llmModels
                      .filter(m => m.provider === 'bedrock')
                      .map(model => (
                        <SelectItem key={model.id} value={model.id}>
                          <div className="flex items-center gap-2">
                            {model.name}
                            {model.recommended && (
                              <Badge variant="secondary" className="text-xs">Recommended</Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {llmModels.find(m => m.id === llmModelId)?.description || 'Select a model to see its description'}
                </p>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <Label>AWS Region</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" className="text-muted-foreground hover:text-foreground">
                        <Info className="w-4 h-4" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80" align="start">
                      <div className="space-y-2 text-sm">
                        <p className="font-medium">Region Selection</p>
                        <p className="text-muted-foreground">
                          Select the AWS region where your <strong>EKS cluster</strong> is deployed. 
                          Bedrock must be accessed from the same region as your cluster.
                        </p>
                        <p className="text-muted-foreground">
                          For air-gapped deployments, the VPC endpoint must also be created in this region.
                        </p>
                        <p className="text-xs text-muted-foreground pt-2 border-t">
                          Note: Not all Bedrock models are available in all regions. 
                          Check <a href="https://docs.aws.amazon.com/bedrock/latest/userguide/models-regions.html" target="_blank" rel="noopener noreferrer" className="text-foreground hover:underline">AWS documentation</a> for model availability.
                        </p>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <Select value={bedrockRegion} onValueChange={setBedrockRegion}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {/* US Regions */}
                    <SelectItem value="us-east-1">US East (N. Virginia) - us-east-1</SelectItem>
                    <SelectItem value="us-east-2">US East (Ohio) - us-east-2</SelectItem>
                    <SelectItem value="us-west-1">US West (N. California) - us-west-1</SelectItem>
                    <SelectItem value="us-west-2">US West (Oregon) - us-west-2</SelectItem>
                    {/* Europe Regions */}
                    <SelectItem value="eu-west-1">EU (Ireland) - eu-west-1</SelectItem>
                    <SelectItem value="eu-west-2">EU (London) - eu-west-2</SelectItem>
                    <SelectItem value="eu-west-3">EU (Paris) - eu-west-3</SelectItem>
                    <SelectItem value="eu-central-1">EU (Frankfurt) - eu-central-1</SelectItem>
                    <SelectItem value="eu-north-1">EU (Stockholm) - eu-north-1</SelectItem>
                    {/* Asia Pacific Regions */}
                    <SelectItem value="ap-south-1">Asia Pacific (Mumbai) - ap-south-1</SelectItem>
                    <SelectItem value="ap-northeast-1">Asia Pacific (Tokyo) - ap-northeast-1</SelectItem>
                    <SelectItem value="ap-northeast-2">Asia Pacific (Seoul) - ap-northeast-2</SelectItem>
                    <SelectItem value="ap-southeast-1">Asia Pacific (Singapore) - ap-southeast-1</SelectItem>
                    <SelectItem value="ap-southeast-2">Asia Pacific (Sydney) - ap-southeast-2</SelectItem>
                    {/* South America */}
                    <SelectItem value="sa-east-1">South America (São Paulo) - sa-east-1</SelectItem>
                    {/* Canada */}
                    <SelectItem value="ca-central-1">Canada (Central) - ca-central-1</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Must match the region where your EKS cluster is deployed
                </p>
              </div>

              {deploymentType === 'airgapped' && (
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <Label>VPC Endpoint URL</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" className="text-muted-foreground hover:text-foreground">
                          <Info className="w-4 h-4" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[28rem]" align="start">
                        <div className="space-y-3">
                          <h4 className="font-semibold">How to Create a Bedrock VPC Endpoint</h4>
                          
                          <div className="space-y-2 text-sm">
                            <p className="font-medium">Step 1: Open VPC Console</p>
                            <p className="text-muted-foreground">
                              Go to <code className="bg-muted px-1 rounded">VPC → Endpoints → Create endpoint</code>
                            </p>
                          </div>
                          
                          <div className="space-y-2 text-sm">
                            <p className="font-medium">Step 2: Configure Endpoint</p>
                            <ul className="text-muted-foreground space-y-1 list-disc list-inside">
                              <li>Name: <code className="bg-muted px-1 rounded">kestrel-bedrock</code></li>
                              <li>Service: Search for <code className="bg-muted px-1 rounded">bedrock-runtime</code></li>
                              <li>Select: <code className="bg-muted px-1 rounded">com.amazonaws.{bedrockRegion}.bedrock-runtime</code></li>
                              <li>VPC: Select your EKS cluster's VPC</li>
                              <li>Subnets: Select private subnets</li>
                              <li>Security Group: Allow HTTPS (443) from cluster</li>
                            </ul>
                          </div>
                          
                          <div className="space-y-2 text-sm">
                            <p className="font-medium">Step 3: Find the Endpoint URL</p>
                            <p className="text-muted-foreground">
                              After creation, go to <strong>VPC → Endpoints</strong> and click on your endpoint. 
                              Under <strong>DNS names</strong>, use the <strong>first regional entry</strong> (without AZ suffix):
                            </p>
                            <code className="block bg-muted px-2 py-1 rounded text-xs break-all">
                              vpce-xxxxx-yyyy.bedrock-runtime.{bedrockRegion}.vpce.amazonaws.com
                            </code>
                            <p className="text-muted-foreground text-xs mt-1">
                              Skip entries with AZ suffixes like <code className="bg-muted px-1 rounded">-us-west-1a</code>. Add <code className="bg-muted px-1 rounded">https://</code> prefix.
                            </p>
                          </div>

                          <div className="pt-2 border-t">
                            <p className="text-xs text-muted-foreground">
                              Or use AWS CLI:
                            </p>
                            <code className="block bg-slate-900 text-slate-100 px-2 py-1 rounded text-xs mt-1 overflow-auto">
                              aws ec2 describe-vpc-endpoints --query "VpcEndpoints[?ServiceName=='com.amazonaws.{bedrockRegion}.bedrock-runtime'].DnsEntries[0].DnsName"
                            </code>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <Input
                    value={bedrockEndpointUrl}
                    onChange={(e) => setBedrockEndpointUrl(e.target.value)}
                    placeholder={`https://vpce-xxx.bedrock-runtime.${bedrockRegion}.vpce.amazonaws.com`}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Required for air-gapped deployments. Click the info icon for setup instructions.
                  </p>
                </div>
              )}
            </>
          )}

          {llmProvider === 'openai' && (
            <>
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <Label>OpenAI API Key *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" className="text-muted-foreground hover:text-foreground">
                        <Info className="w-4 h-4" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80" align="start">
                      <div className="space-y-2 text-sm">
                        <p className="font-medium">OpenAI API Key</p>
                        <p className="text-muted-foreground">
                          Your OpenAI API key is required to authenticate with the OpenAI API.
                          This key will be stored as a Kubernetes Secret in your cluster.
                        </p>
                        <p className="text-muted-foreground">
                          Get your API key from{' '}
                          <a 
                            href="https://platform.openai.com/api-keys" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-foreground hover:underline"
                          >
                            platform.openai.com/api-keys
                          </a>
                        </p>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <Input
                  type="password"
                  value={openaiApiKey}
                  onChange={(e) => setOpenaiApiKey(e.target.value)}
                  placeholder="sk-..."
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Your API key will be stored securely as a Kubernetes Secret
                </p>
              </div>

              <div>
                <Label className="mb-1.5 block">Model</Label>
                <Select value={openaiModelId} onValueChange={setOpenaiModelId}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gpt-5.2-2025-12-11">
                      <div className="flex items-center gap-2">
                        GPT-5.2
                        <Badge variant="secondary" className="text-xs">Recommended</Badge>
                      </div>
                    </SelectItem>
                    <SelectItem value="gpt-5-2025-08-07">GPT-5 (Intelligent reasoning)</SelectItem>
                    <SelectItem value="gpt-5-mini-2025-08-07">GPT-5 Mini (Faster, cost-efficient)</SelectItem>
                    <SelectItem value="gpt-4.1">GPT-4.1 (Non-reasoning)</SelectItem>
                    <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                    <SelectItem value="gpt-4.1-mini">GPT-4.1 Mini (Faster, cheaper)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  GPT-5.2 is recommended for best coding and agentic performance
                </p>
              </div>
            </>
          )}
        </div>

        {/* Validation warning for OpenAI API key */}
        {llmProvider === 'openai' && !openaiApiKey && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> OpenAI API key is required to use OpenAI models.
            </p>
          </div>
        )}

        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCurrentStep(1)}>
            Back
          </Button>
          <Button 
            onClick={async () => {
              await handleSaveDeploymentConfig();
              handleStepComplete(2);
            }} 
            className="flex-1"
            disabled={llmProvider === 'openai' && !openaiApiKey}
          >
            Continue <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderInfrastructureStep = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="w-5 h-5" />
          Infrastructure Configuration
        </CardTitle>
        <CardDescription>
          Configure your deployment infrastructure.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          {/* Required: Container Registry */}
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <Label>{currentCloud.registryName} URL *</Label>
              {deploymentType === 'airgapped' && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" className="text-muted-foreground hover:text-foreground">
                      <Info className="w-4 h-4" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[28rem]" align="start">
                    <div className="space-y-3">
                      <h4 className="font-semibold">ECR Setup for Air-Gapped Environments</h4>
                      <p className="text-sm text-muted-foreground">
                        For air-gapped deployments, your EKS cluster needs VPC endpoints to pull images from ECR without internet access.
                      </p>
                      
                      <div className="space-y-2 text-sm">
                        <p className="font-medium">Required VPC Endpoints</p>
                        <p className="text-muted-foreground">Create these 3 endpoints in your VPC:</p>
                        <ul className="text-muted-foreground space-y-1 list-disc list-inside">
                          <li><code className="bg-muted px-1 rounded">com.amazonaws.{bedrockRegion}.ecr.api</code> (Interface)</li>
                          <li><code className="bg-muted px-1 rounded">com.amazonaws.{bedrockRegion}.ecr.dkr</code> (Interface)</li>
                          <li><code className="bg-muted px-1 rounded">com.amazonaws.{bedrockRegion}.s3</code> (Gateway)</li>
                        </ul>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <p className="font-medium">Configuration</p>
                        <ul className="text-muted-foreground space-y-1 list-disc list-inside">
                          <li>VPC: Same as your EKS cluster</li>
                          <li>Subnets: Private subnets where EKS nodes run</li>
                          <li>Security Group: Allow HTTPS (443) from EKS nodes</li>
                          <li>Enable <strong>Private DNS</strong> for interface endpoints</li>
                        </ul>
                      </div>

                      <div className="pt-2 border-t">
                        <p className="text-xs text-muted-foreground">
                          The S3 gateway endpoint is required because ECR stores image layers in S3. With these endpoints, 
                          your cluster can pull images using the standard ECR URL without internet access.
                        </p>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
            <Input
              value={ecrRegistryUrl}
              onChange={(e) => setEcrRegistryUrl(e.target.value)}
              placeholder={currentCloud.registryPlaceholder}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Enter only the base URL (e.g., <code className="bg-muted px-1 rounded">{currentCloud.registryPlaceholder}</code>). 
              You'll create repositories under <code className="bg-muted px-1 rounded">kestrel/</code> (e.g., <code className="bg-muted px-1 rounded">kestrel/server</code>) in the Download & Deploy step.
              {deploymentType === 'airgapped' && ' Click the info icon for air-gapped setup.'}
            </p>
          </div>

          {/* Optional: IAM/Service Account (not required for Node IAM Role method) */}
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <Label>{currentCloud.iamLabel}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className="text-muted-foreground hover:text-foreground">
                    <Info className="w-4 h-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[34rem]" align="start" side="bottom" sideOffset={5} collisionPadding={20}>
                  <div className="space-y-3 max-h-[60vh] overflow-y-auto p-1">
                    <h4 className="font-semibold">IAM Permissions for Kestrel Services</h4>
                    <p className="text-sm text-muted-foreground">
                      Kestrel needs permissions to access Bedrock and Secrets Manager. Choose the method that fits your environment.
                    </p>
                    
                    <div className="space-y-2 text-sm">
                      <p className="font-medium">Required IAM Policy</p>
                      <p className="text-muted-foreground">Create a policy named <code className="bg-muted px-1 rounded">KestrelServicePolicy</code>:</p>
                      <pre className="bg-slate-900 text-slate-100 p-2 rounded text-xs overflow-auto max-h-48">
{`{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Effect": "Allow",
      "Action": ["bedrock:InvokeModel", "bedrock:InvokeModelWithResponseStream"],
      "Resource": [
        "arn:aws:bedrock:${bedrockRegion}::foundation-model/*",
        "arn:aws:bedrock:${bedrockRegion}:*:inference-profile/*"
      ]
    },
    {
      "Effect": "Allow",
      "Action": ["aws-marketplace:ViewSubscriptions", "aws-marketplace:Subscribe"],
      "Resource": "*"
    },
    {
      "Effect": "Allow",
      "Action": ["secretsmanager:GetSecretValue", "secretsmanager:DescribeSecret"],
      "Resource": "arn:aws:secretsmanager:${bedrockRegion}:*:secret:kestrel/*"
    }
  ]
}`}
                      </pre>
                    </div>

                    <div className="space-y-2 text-sm border-t pt-3">
                      <p className="font-medium">Option A: IRSA (Recommended)</p>
                      <p className="text-muted-foreground text-xs">Most secure - pod-level permissions via OIDC. Requires EKS OIDC provider.</p>
                      <ol className="text-muted-foreground space-y-1 list-decimal list-inside text-xs">
                        <li>Enable OIDC: <code className="bg-muted px-1 rounded">eksctl utils associate-iam-oidc-provider --cluster your-cluster --approve</code></li>
                        <li>Go to <strong>IAM → Roles → Create role</strong></li>
                        <li>Select <strong>Web identity</strong>, choose your EKS OIDC provider</li>
                        <li>Audience: <code className="bg-muted px-1 rounded">sts.amazonaws.com</code></li>
                        <li>Attach <code className="bg-muted px-1 rounded">KestrelServicePolicy</code>, name: <code className="bg-muted px-1 rounded">kestrel-service-role</code></li>
                        <li>Enter the role ARN below - it will be applied to both server and agents service accounts</li>
                      </ol>
                    </div>
                    
                    <div className="space-y-2 text-sm border-t pt-3">
                      <p className="font-medium">Option B: EKS Pod Identity (Simpler)</p>
                      <p className="text-muted-foreground text-xs">No OIDC setup required. Available in EKS 1.24+.</p>
                      <ol className="text-muted-foreground space-y-1 list-decimal list-inside text-xs">
                        <li>Install Pod Identity Agent: <code className="bg-muted px-1 rounded">aws eks create-addon --cluster-name your-cluster --addon-name eks-pod-identity-agent</code></li>
                        <li>Create IAM role with <code className="bg-muted px-1 rounded">KestrelServicePolicy</code> (trust policy: <code className="bg-muted px-1 rounded">pods.eks.amazonaws.com</code>)</li>
                        <li>Associate for server: <code className="bg-muted px-1 rounded">aws eks create-pod-identity-association --cluster-name your-cluster --namespace kestrel --service-account kestrel-autonp-server --role-arn arn:aws:iam::ACCOUNT:role/kestrel-service-role</code></li>
                        <li>Associate for agents: <code className="bg-muted px-1 rounded">aws eks create-pod-identity-association --cluster-name your-cluster --namespace kestrel --service-account kestrel-autonp-agents --role-arn arn:aws:iam::ACCOUNT:role/kestrel-service-role</code></li>
                      </ol>
                    </div>

                    <div className="space-y-2 text-sm border-t pt-3 pb-4">
                      <p className="font-medium">Option C: Node IAM Role</p>
                      <p className="text-muted-foreground text-xs">Attach policy to EKS node role. Requires IMDS configuration for pods.</p>
                      <ol className="text-muted-foreground space-y-1 list-decimal list-inside text-xs">
                        <li>Find your node IAM role: <strong>EKS → Cluster → Compute → Node group → Node IAM role</strong></li>
                        <li>Attach <code className="bg-muted px-1 rounded">KestrelServicePolicy</code> to this role</li>
                        <li><strong>Required:</strong> Update node group launch template to set <code className="bg-muted px-1 rounded">HttpPutResponseHopLimit: 2</code> (default is 1, which blocks pods)</li>
                        <li>Leave the IAM Role ARN field above empty</li>
                      </ol>
                      <p className="text-amber-600 text-xs mt-1">
                        <strong>Important:</strong> EKS defaults block IMDS from pods. You must set HttpPutResponseHopLimit=2 in your launch template.
                      </p>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <Input
              value={iamRoleArn}
              onChange={(e) => setIamRoleArn(e.target.value)}
              placeholder={currentCloud.iamPlaceholder}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Required for IRSA or Pod Identity. For Node IAM Role, leave empty but ensure HttpPutResponseHopLimit=2 in your node group. Click info icon for setup guide.
            </p>
          </div>

          {/* Database Configuration */}
          <div className="border rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Database Configuration</Label>
                <p className="text-xs text-muted-foreground">
                  PostgreSQL and Redis are included with the Kestrel Helm chart
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div
                className={`p-3 border rounded-lg cursor-pointer transition-all ${
                  !useExternalDatabase
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setUseExternalDatabase(false)}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    !useExternalDatabase ? 'border-primary bg-primary' : 'border-gray-300'
                  }`} />
                  <div>
                    <p className="font-medium">Use Bundled Database (Recommended)</p>
                    <p className="text-xs text-muted-foreground">
                      PostgreSQL and Redis will be deployed alongside Kestrel services
                    </p>
                  </div>
                </div>
              </div>

              <div
                className={`p-3 border rounded-lg cursor-pointer transition-all ${
                  useExternalDatabase
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setUseExternalDatabase(true)}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    useExternalDatabase ? 'border-primary bg-primary' : 'border-gray-300'
                  }`} />
                  <div>
                    <p className="font-medium">Use External Database</p>
                    <p className="text-xs text-muted-foreground">
                      Connect to your own RDS and ElastiCache instances (production recommended)
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {useExternalDatabase && (
              <div className="space-y-4 pt-4 border-t">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>PostgreSQL Host</Label>
                    <Input
                      value={postgresHost}
                      onChange={(e) => setPostgresHost(e.target.value)}
                      placeholder="your-rds-endpoint.rds.amazonaws.com"
                    />
                  </div>
                  <div>
                    <Label>Port</Label>
                    <Input
                      type="number"
                      value={postgresPort}
                      onChange={(e) => setPostgresPort(parseInt(e.target.value))}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Database Name</Label>
                    <Input
                      value={postgresDb}
                      onChange={(e) => setPostgresDb(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Username</Label>
                    <Input
                      value={postgresUser}
                      onChange={(e) => setPostgresUser(e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Redis Host</Label>
                    <Input
                      value={redisHost}
                      onChange={(e) => setRedisHost(e.target.value)}
                      placeholder="your-elasticache-endpoint.cache.amazonaws.com"
                    />
                  </div>
                  <div>
                    <Label>Port</Label>
                    <Input
                      type="number"
                      value={redisPort}
                      onChange={(e) => setRedisPort(parseInt(e.target.value))}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Elasticsearch/OpenSearch Configuration (Optional) */}
          <div className="border rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Elasticsearch / OpenSearch (Optional)</Label>
                <p className="text-xs text-muted-foreground">
                  Enable for advanced log analysis and incident detection
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowElasticsearchInstructions(!showElasticsearchInstructions)}
              >
                {showElasticsearchInstructions ? 'Hide' : 'Setup Guide'}
              </Button>
            </div>

            {showElasticsearchInstructions && (
              <Alert className="bg-muted/50 border-border">
                <AlertCircle className="h-4 w-4 text-muted-foreground" />
                <AlertTitle className="text-foreground">Amazon OpenSearch Service Setup</AlertTitle>
                <AlertDescription className="text-muted-foreground space-y-3">
                  <p className="font-medium mt-2">Step 1: Create OpenSearch Domain</p>
                  <ol className="list-decimal list-inside space-y-1 text-sm">
                    <li>Go to <strong>Amazon OpenSearch Service</strong> in AWS Console (region: <code className="bg-muted px-1 rounded">{bedrockRegion}</code>)</li>
                    <li>Click <strong>Create domain</strong></li>
                    <li>Domain name: <code className="bg-muted px-1 rounded">kestrel-logs</code></li>
                    <li>Template: <strong>Dev/test</strong> for testing, <strong>Production</strong> for prod</li>
                    <li>Version: OpenSearch 2.x (or Elasticsearch 7.x)</li>
                    <li>Instance type: <code className="bg-muted px-1 rounded">t3.small.search</code> (dev) or <code className="bg-muted px-1 rounded">r6g.large.search</code> (prod)</li>
                  </ol>
                  
                  <p className="font-medium mt-3">Step 2: Network Configuration</p>
                  <ol className="list-decimal list-inside space-y-1 text-sm">
                    <li>Network: Select <strong>VPC access</strong></li>
                    <li>VPC: Choose the <strong>same VPC as your EKS cluster</strong></li>
                    <li>IP address type: <strong>IPv4 only</strong> (recommended), or Dual-stack if your subnets have IPv6 CIDRs</li>
                    <li>Subnets: Select subnets where EKS nodes can reach OpenSearch</li>
                    <li>Security groups: Select or create a security group that allows <strong>HTTPS (port 443)</strong> inbound from your EKS node security group</li>
                  </ol>
                  <p className="text-xs mt-1">
                    VPC access ensures OpenSearch is not publicly accessible. No separate VPC endpoint is needed when in the same VPC as EKS.
                  </p>

                  <p className="font-medium mt-3">Step 3: Access Control</p>
                  <ol className="list-decimal list-inside space-y-1 text-sm">
                    <li>Enable <strong>Fine-grained access control</strong></li>
                    <li>Create master user: Choose <strong>Create master user</strong></li>
                    <li>Set a username and password (you'll enter these below)</li>
                    <li>Access policy: Select <strong>Only use fine-grained access control</strong></li>
                  </ol>
                  <p className="text-xs mt-1">
                    Fine-grained access control with master user uses username/password authentication. No additional IAM permissions are needed for Kestrel.
                  </p>

                  <p className="font-medium mt-3">Step 4: Get Your Endpoint</p>
                  <p className="text-sm">After creation (~15-20 min), go to your domain and copy the <strong>Domain endpoint</strong>:</p>
                  <code className="block bg-muted px-2 py-1 rounded text-xs mt-1">
                    https://vpc-kestrel-logs-xxxxx.{bedrockRegion}.es.amazonaws.com
                  </code>
                </AlertDescription>
              </Alert>
            )}

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="elasticsearch-enabled"
                checked={elasticsearchEnabled}
                onChange={(e) => setElasticsearchEnabled(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300"
              />
              <Label htmlFor="elasticsearch-enabled" className="cursor-pointer">
                Enable Elasticsearch / OpenSearch integration
              </Label>
            </div>

            {elasticsearchEnabled && (
              <div className="space-y-4 pt-2">
                <div>
                  <Label>OpenSearch Endpoint URL</Label>
                  <Input
                    value={elasticsearchEndpoint}
                    onChange={(e) => setElasticsearchEndpoint(e.target.value)}
                    placeholder={`https://vpc-kestrel-logs-xxxxx.${bedrockRegion}.es.amazonaws.com`}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Your Amazon OpenSearch Service domain endpoint (include https://)
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Master Username</Label>
                    <Input
                      value={elasticsearchUsername}
                      onChange={(e) => setElasticsearchUsername(e.target.value)}
                      placeholder="admin"
                    />
                  </div>
                  <div>
                    <Label>Master Password</Label>
                    <Input
                      type="password"
                      value={elasticsearchPassword}
                      onChange={(e) => setElasticsearchPassword(e.target.value)}
                      placeholder="••••••••"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Store in AWS Secrets Manager for production
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Ingress Configuration */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Label>Ingress Class</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" className="text-muted-foreground hover:text-foreground">
                      <Info className="w-4 h-4" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[26rem]" align="start">
                    <div className="space-y-3 py-2">
                      <p className="font-medium">What is Ingress Class?</p>
                      <p className="text-sm text-muted-foreground">
                        The Ingress Class determines which ingress controller handles traffic routing to Kestrel. 
                        Choose the one installed in your cluster.
                      </p>
                      <div className="text-sm space-y-2">
                        <p className="font-medium">How to find your Ingress Class:</p>
                        <code className="block bg-muted p-2 rounded text-xs">
                          kubectl get ingressclass
                        </code>
                        <p className="text-muted-foreground">
                          Look for the <code className="bg-muted px-1 rounded">NAME</code> column. Common values:
                        </p>
                        <ul className="list-disc list-inside text-muted-foreground space-y-1">
                          <li><strong>nginx</strong> - NGINX Ingress Controller (most common)</li>
                          <li><strong>alb</strong> - AWS ALB Ingress Controller</li>
                          <li><strong>kong</strong> - Kong Ingress Controller</li>
                        </ul>
                        <p className="text-muted-foreground mt-2">
                          If no ingress class exists, you'll need to install an ingress controller first 
                          (e.g., <code className="bg-muted px-1 rounded">helm install ingress-nginx ingress-nginx/ingress-nginx</code>).
                        </p>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <Select value={ingressClassName} onValueChange={setIngressClassName}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nginx">nginx</SelectItem>
                  <SelectItem value="alb">alb (AWS ALB)</SelectItem>
                  <SelectItem value="kong">kong</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Label>External Access Hostname (Optional)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" className="text-muted-foreground hover:text-foreground">
                      <Info className="w-4 h-4" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[28rem]" align="start" collisionPadding={20}>
                    <div className="space-y-3 py-2 max-h-[70vh] overflow-y-auto">
                      <p className="font-medium">What is External Access Hostname?</p>
                      <p className="text-sm text-muted-foreground">
                        A hostname for external services to reach your Kestrel deployment. This is <strong>optional</strong> and only needed if you plan to use:
                      </p>
                      <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                        <li><strong>Slack Integration:</strong> For OAuth callbacks and slash commands</li>
                        <li><strong>GitHub Integration:</strong> For webhooks from your GitHub App</li>
                      </ul>
                      
                      <div className="text-sm space-y-2 border-t pt-2">
                        <p className="font-medium">How to Set Up:</p>
                        <ol className="text-muted-foreground text-xs list-decimal list-inside space-y-1">
                          <li>Create a public DNS record (e.g., <code className="bg-muted px-1 rounded">kestrel.yourcompany.com</code>)</li>
                          <li>Point it to a public-facing load balancer or ingress</li>
                          <li>Configure TLS certificate (recommended: AWS ACM or Let's Encrypt)</li>
                          <li>Route traffic to the Kestrel frontend service on port 80/443</li>
                        </ol>
                        <p className="text-muted-foreground text-xs mt-1">
                          <strong>Note:</strong> This hostname must be accessible from the internet for Slack/GitHub webhooks to work.
                        </p>
                      </div>
                      
                      <div className="text-sm space-y-2 border-t pt-2">
                        <p className="font-medium">When to Skip This:</p>
                        <p className="text-muted-foreground text-xs">
                          If you're only using Kestrel for Kubernetes monitoring and cloud security without Slack/GitHub integrations, 
                          you can leave this blank. The Kestrel UI is accessible via port-forward or internal load balancer.
                        </p>
                      </div>
                      
                      <div className="text-sm space-y-2 border-t pt-2">
                        <p className="font-medium">Note on Kestrel Operator Connectivity:</p>
                        <p className="text-muted-foreground text-xs">
                          Kestrel Operators do <strong>not</strong> use this external hostname. How they connect depends on where they are deployed:
                        </p>
                        <ul className="text-muted-foreground text-xs list-disc list-inside space-y-0.5 mt-1">
                          <li><strong>Same cluster:</strong> Internal K8s service DNS</li>
                          <li><strong>Same VPC, different cluster:</strong> Internal NLB DNS</li>
                          <li><strong>Different VPC:</strong> VPC Peering, Transit Gateway, or PrivateLink</li>
                          <li><strong>Air-gapped:</strong> Custom internal DNS (e.g., Route 53 Private Hosted Zone)</li>
                        </ul>
                        <p className="text-muted-foreground text-xs mt-1">
                          Kestrel Operator connectivity is configured during cluster onboarding in the Kestrel UI.
                        </p>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <Input
                value={ingressHost}
                onChange={(e) => setIngressHost(e.target.value)}
                placeholder="kestrel.yourcompany.com (optional)"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Only required for Slack/GitHub integrations. Leave blank if not using these features.
              </p>
            </div>
          </div>

        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCurrentStep(2)}>
            Back
          </Button>
          <Button 
            onClick={async () => {
              await handleSaveDeploymentConfig(true); // Save infrastructure config
              handleStepComplete(3);
            }} 
            className="flex-1"
            disabled={!ecrRegistryUrl || (elasticsearchEnabled && !elasticsearchEndpoint)}
          >
            Continue <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderGitHubIntegrationStep = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GitBranch className="w-5 h-5" />
          GitHub Integration
          <Badge variant="outline" className="ml-2">Optional</Badge>
        </CardTitle>
        <CardDescription>
          {deploymentType === 'airgapped' 
            ? 'Connect your GitHub Enterprise Server for IaC analysis and GitOps workflows'
            : 'Connect to GitHub.com for IaC analysis and GitOps workflows'
          }
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Air-gapped: GitHub Enterprise Server */}
        {deploymentType === 'airgapped' ? (
          <>
            <Alert className="bg-muted/50 border-border">
              <Info className="h-4 w-4 text-muted-foreground" />
              <AlertTitle className="text-foreground">GitHub Enterprise Server Required</AlertTitle>
              <AlertDescription className="text-muted-foreground text-sm">
                Air-gapped deployments require <strong>GitHub Enterprise Server</strong> deployed in your AWS environment.
                This enables IaC repository analysis, automatic PR creation, and GitOps workflows without internet access.
              </AlertDescription>
            </Alert>

            {/* Network Connectivity Section */}
            <div className="space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <Network className="w-4 h-4" />
                Network Connectivity Requirements
              </h4>
              
              <div className="border rounded-lg p-4 space-y-4">
                <p className="text-sm text-muted-foreground">
                  Kestrel and GitHub Enterprise Server need <strong>bidirectional network connectivity</strong>:
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-muted/50 p-3 rounded-lg">
                    <p className="text-sm font-medium mb-2">Kestrel → GitHub Enterprise</p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>• API calls to read repository files</li>
                      <li>• Create pull requests for IaC changes</li>
                      <li>• Manage GitHub App installations</li>
                      <li>• Port: <code className="bg-muted px-1 rounded">443 (HTTPS)</code></li>
                    </ul>
                  </div>
                  <div className="bg-muted/50 p-3 rounded-lg">
                    <p className="text-sm font-medium mb-2">GitHub Enterprise → Kestrel</p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>• Webhook events for repository changes</li>
                      <li>• App installation/uninstallation events</li>
                      <li>• Pull request status updates</li>
                      <li>• Port: <code className="bg-muted px-1 rounded">443 (HTTPS)</code></li>
                    </ul>
                  </div>
                </div>

                {/* Same VPC vs Different VPC */}
                <Tabs defaultValue="same-vpc" className="mt-4">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="same-vpc">Same VPC</TabsTrigger>
                    <TabsTrigger value="different-vpc">Different VPCs</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="same-vpc" className="mt-4 space-y-3">
                    <Alert className="bg-green-50 border-green-200">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <AlertTitle className="text-green-800">Simplest Setup</AlertTitle>
                      <AlertDescription className="text-green-700 text-sm">
                        If GitHub Enterprise Server is in the same VPC as your EKS cluster, you only need to configure security groups.
                      </AlertDescription>
                    </Alert>
                    <div className="text-sm space-y-2">
                      <p className="font-medium">Security Group Configuration:</p>
                      <pre className="p-3 bg-slate-900 text-slate-100 rounded text-xs overflow-auto">
{`# Allow Kestrel (EKS nodes) to reach GHE
aws ec2 authorize-security-group-ingress \\
  --group-id <GHE_SECURITY_GROUP_ID> \\
  --protocol tcp --port 443 \\
  --source-group <EKS_NODE_SECURITY_GROUP_ID>

# Allow GHE to reach Kestrel (for webhooks)
aws ec2 authorize-security-group-ingress \\
  --group-id <EKS_NODE_SECURITY_GROUP_ID> \\
  --protocol tcp --port 443 \\
  --source-group <GHE_SECURITY_GROUP_ID>`}
                      </pre>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="different-vpc" className="mt-4 space-y-3">
                    <Alert className="bg-amber-50 border-amber-200">
                      <AlertCircle className="h-4 w-4 text-amber-600" />
                      <AlertTitle className="text-amber-800">Additional Setup Required</AlertTitle>
                      <AlertDescription className="text-amber-700 text-sm">
                        Cross-VPC connectivity requires VPC Peering or Transit Gateway configuration.
                      </AlertDescription>
                    </Alert>
                    
                    <div className="text-sm space-y-4">
                      <div>
                        <p className="font-medium mb-2">Step 1: Create VPC Peering Connection</p>
                        <pre className="p-3 bg-slate-900 text-slate-100 rounded text-xs overflow-auto">
{`# Create peering connection from EKS VPC to GHE VPC
aws ec2 create-vpc-peering-connection \\
  --vpc-id <EKS_VPC_ID> \\
  --peer-vpc-id <GHE_VPC_ID> \\
  --region ${bedrockRegion || 'us-west-1'}

# Accept the peering connection
aws ec2 accept-vpc-peering-connection \\
  --vpc-peering-connection-id <PEERING_CONNECTION_ID>`}
                        </pre>
                      </div>
                      
                      <div>
                        <p className="font-medium mb-2">Step 2: Update Route Tables</p>
                        <pre className="p-3 bg-slate-900 text-slate-100 rounded text-xs overflow-auto">
{`# Add route in EKS VPC route table to GHE VPC CIDR
aws ec2 create-route \\
  --route-table-id <EKS_ROUTE_TABLE_ID> \\
  --destination-cidr-block <GHE_VPC_CIDR> \\
  --vpc-peering-connection-id <PEERING_CONNECTION_ID>

# Add route in GHE VPC route table to EKS VPC CIDR  
aws ec2 create-route \\
  --route-table-id <GHE_ROUTE_TABLE_ID> \\
  --destination-cidr-block <EKS_VPC_CIDR> \\
  --vpc-peering-connection-id <PEERING_CONNECTION_ID>`}
                        </pre>
                      </div>
                      
                      <div>
                        <p className="font-medium mb-2">Step 3: Configure Security Groups</p>
                        <pre className="p-3 bg-slate-900 text-slate-100 rounded text-xs overflow-auto">
{`# Allow traffic from EKS VPC CIDR to GHE
aws ec2 authorize-security-group-ingress \\
  --group-id <GHE_SECURITY_GROUP_ID> \\
  --protocol tcp --port 443 \\
  --cidr <EKS_VPC_CIDR>

# Allow traffic from GHE VPC CIDR to EKS (for webhooks)
aws ec2 authorize-security-group-ingress \\
  --group-id <EKS_NODE_SECURITY_GROUP_ID> \\
  --protocol tcp --port 443 \\
  --cidr <GHE_VPC_CIDR>`}
                        </pre>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            </div>

            {/* GitHub App Setup */}
            <div className="space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <Key className="w-4 h-4" />
                Create GitHub App on Enterprise Server
              </h4>
              
              <div className="border rounded-lg p-4 space-y-4">
                <div className="space-y-3">
                  <p className="text-sm font-medium">Step 1: Create the GitHub App</p>
                  <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-2">
                    <li>Navigate to your GitHub Enterprise Server web interface</li>
                    <li>Click your <strong>profile avatar</strong> (top right) → <strong>Settings</strong></li>
                    <li>In the left sidebar, scroll to <strong>Developer settings</strong></li>
                    <li>Click <strong>GitHub Apps</strong> → <strong>New GitHub App</strong></li>
                    <li>
                      Configure the app with these settings:
                      <ul className="list-disc list-inside ml-4 mt-1 space-y-0.5 text-xs">
                        <li><strong>GitHub App name:</strong> <code className="bg-muted px-1 rounded">Kestrel</code></li>
                        <li><strong>Homepage URL:</strong> <code className="bg-muted px-1 rounded">https://{ingressHost || 'kestrel.yourcompany.com'}</code></li>
                        <li><strong>Callback URL:</strong> Leave blank (not required)</li>
                        <li><strong>Webhook URL:</strong> <code className="bg-muted px-1 rounded">https://{ingressHost || 'kestrel.yourcompany.com'}/api/webhooks/github</code></li>
                        <li><strong>Webhook secret:</strong> Generate a secure random string (e.g., <code className="bg-muted px-1 rounded">openssl rand -hex 32</code>)</li>
                      </ul>
                    </li>
                    <li>
                      Under <strong>"Identifying and authorizing users"</strong>, leave all options <strong>unchecked</strong>:
                      <ul className="list-disc list-inside ml-4 mt-1 space-y-0.5 text-xs text-muted-foreground">
                        <li>Expire user authorization tokens - unchecked</li>
                        <li>Request user authorization (OAuth) during installation - unchecked</li>
                        <li>Enable Device Flow - unchecked</li>
                      </ul>
                    </li>
                    <li>
                      Set <strong>Repository permissions</strong>:
                      <ul className="list-disc list-inside ml-4 mt-1 space-y-0.5 text-xs">
                        <li><code className="bg-muted px-1 rounded">Contents: Read & write</code></li>
                        <li><code className="bg-muted px-1 rounded">Pull requests: Read & write</code></li>
                        <li><code className="bg-muted px-1 rounded">Metadata: Read-only</code></li>
                      </ul>
                    </li>
                    <li>Click <strong>Create GitHub App</strong></li>
                    <li>Note the <strong>App ID</strong> from the app settings page</li>
                    <li>Click <strong>Generate a private key</strong> and save the .pem file</li>
                  </ol>
                </div>

                <div className="space-y-3">
                  <p className="text-sm font-medium">Step 2: Configure GitHub Enterprise Settings</p>
                  <p className="text-sm text-muted-foreground mb-3">
                    Enter the details from your GitHub App:
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="github-enterprise-url">GitHub Enterprise Server URL</Label>
                      <Input
                        id="github-enterprise-url"
                        value={githubEnterpriseUrl}
                        onChange={(e) => setGithubEnterpriseUrl(e.target.value)}
                        placeholder="https://github.yourcompany.com"
                        className="mt-1"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        The base URL of your GitHub Enterprise Server (without trailing slash)
                      </p>
                    </div>
                    
                    <div>
                      <Label htmlFor="github-app-id">GitHub App ID</Label>
                      <Input
                        id="github-app-id"
                        value={githubAppId}
                        onChange={(e) => setGithubAppId(e.target.value)}
                        placeholder="123456"
                        className="mt-1"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Found on your GitHub App's settings page (numeric ID at the top)
                      </p>
                    </div>
                    
                    <div>
                      <Label htmlFor="github-app-slug">GitHub App Slug</Label>
                      <Input
                        id="github-app-slug"
                        value={githubAppSlug}
                        onChange={(e) => setGithubAppSlug(e.target.value)}
                        placeholder="kestrel"
                        className="mt-1"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        URL-friendly name from <code className="bg-muted px-1 rounded">{githubEnterpriseUrl || 'https://github.yourcompany.com'}/apps/<strong>{githubAppSlug || 'your-app-slug'}</strong></code>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="text-sm font-medium">Step 3: Store Secrets in AWS Secrets Manager</p>
                  <p className="text-sm text-muted-foreground mb-2">
                    Store the sensitive credentials (private key and webhook secret):
                  </p>
                  <pre className="p-3 bg-slate-900 text-slate-100 rounded text-xs overflow-auto">
{`# Store the private key (.pem file you downloaded)
aws secretsmanager create-secret \\
  --name kestrel/github-app-private-key \\
  --secret-string file://path/to/private-key.pem \\
  --region ${bedrockRegion || 'us-west-1'}

# Store the webhook secret (the one you generated when creating the app)
aws secretsmanager create-secret \\
  --name kestrel/github-app-webhook-secret \\
  --secret-string "YOUR_WEBHOOK_SECRET" \\
  --region ${bedrockRegion || 'us-west-1'}`}
                  </pre>
                </div>

                <div className="space-y-3">
                  <p className="text-sm font-medium">Step 4: Add TLS Certificate to GitHub Enterprise Server</p>
                  <p className="text-sm text-muted-foreground mb-2">
                    Kestrel generates a TLS certificate for your ingress hostname. The CA certificate must be added
                    to GitHub Enterprise Server's trusted certificates for webhook delivery to work:
                  </p>
                  
                  <div className="mb-3">
                    {ingressHost ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleGenerateAndDownloadTLSCert}
                        disabled={generatingTLSCert}
                      >
                        {generatingTLSCert ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        {generatingTLSCert ? 'Generating...' : 'Download CA Certificate (kestrel-ca.crt)'}
                      </Button>
                    ) : (
                      <Alert variant="destructive" className="py-2">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription className="text-xs">
                          Please configure the <strong>Ingress Hostname</strong> in the Infrastructure step first.
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                  
                  <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-2">
                    <li>Click the button above to generate and download the CA certificate</li>
                    <li>Copy <code className="bg-muted px-1 rounded">kestrel-ca.crt</code> to your GitHub Enterprise Server</li>
                    <li>SSH into GitHub Enterprise Server: <code className="bg-muted px-1 rounded">ssh -p 122 admin@&lt;ghe-hostname&gt;</code></li>
                    <li>Install the CA certificate: <code className="bg-muted px-1 rounded">ghe-ssl-ca-certificate-install -c /path/to/kestrel-ca.crt</code></li>
                    <li>Apply configuration: <code className="bg-muted px-1 rounded">ghe-config-apply</code></li>
                  </ol>
                  <Alert className="mt-2">
                    <Info className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      This step is required for GitHub Enterprise Server to trust webhook connections to your Kestrel instance.
                      Without this, webhook delivery will fail with a certificate error.
                    </AlertDescription>
                  </Alert>
                </div>

                <div className="space-y-3">
                  <p className="text-sm font-medium">Step 5: Install the GitHub App</p>
                  <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
                    <li>On your GitHub Enterprise Server, go to the app's settings page</li>
                    <li>Click <strong>Install App</strong> in the left sidebar</li>
                    <li>Select your organization and grant access to repositories</li>
                    <li>After deploying Kestrel, verify in <strong>Settings → CI/CD Integrations</strong></li>
                  </ol>
                </div>
              </div>
            </div>

            <Alert className="bg-green-50 border-green-200">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertTitle className="text-green-800">Air-Gapped Compatible</AlertTitle>
              <AlertDescription className="text-green-700 text-sm">
                The GitHub Enterprise settings you configured above will be included in your generated Helm values.
                Kestrel automatically routes all GitHub API calls to your Enterprise Server.
              </AlertDescription>
            </Alert>
          </>
        ) : (
          /* Non-airgapped: GitHub.com */
          <>
            <Alert>
              <Info className="h-4 w-4" />
              <AlertTitle>GitHub.com Integration</AlertTitle>
              <AlertDescription className="text-sm">
                For non-airgapped deployments, Kestrel connects to <strong>GitHub.com</strong> for IaC analysis and GitOps workflows.
                You'll need to create a GitHub App on github.com and configure it after deployment.
              </AlertDescription>
            </Alert>

            <div className="border rounded-lg p-4 space-y-4">
              <p className="text-sm text-muted-foreground">
                GitHub integration can be configured after deployment via <strong>Integrations → CI/CD Integrations</strong> in the Kestrel UI.
                The setup process involves:
              </p>
              <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-2">
                <li>Creating a GitHub App on <a href="https://github.com/settings/apps/new" target="_blank" rel="noopener noreferrer" className="text-foreground underline inline-flex items-center gap-1">github.com <ExternalLink className="w-3 h-3" /></a></li>
                <li>Configuring webhook URL and permissions</li>
                <li>Storing credentials in AWS Secrets Manager</li>
                <li>Installing the app on your organization</li>
              </ol>
              <p className="text-xs text-muted-foreground mt-2">
                Detailed instructions will be provided in the Download & Deploy step.
              </p>
            </div>
          </>
        )}

        <div className="flex gap-2 pt-4">
          <Button variant="outline" onClick={() => setCurrentStep(3)}>
            Back
          </Button>
          <Button 
            onClick={async () => {
              await handleSaveDeploymentConfig(true); // Save GitHub Enterprise config
              handleStepComplete(4);
            }} 
            className="flex-1"
          >
            Continue <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderGenerateStep = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileCode className="w-5 h-5" />
          Generate Configuration
        </CardTitle>
        <CardDescription>
          Generate customized Helm values based on your configuration.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="p-4 bg-muted rounded-lg">
          <h4 className="font-medium mb-4">Configuration Summary</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Cloud Provider:</span>
              <span className="ml-2 font-medium">{currentCloud.shortName}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Deployment Type:</span>
              <span className="ml-2 font-medium capitalize">{deploymentType}</span>
            </div>
            <div>
              <span className="text-muted-foreground">LLM Provider:</span>
              <span className="ml-2 font-medium capitalize">{llmProvider}</span>
            </div>
            <div>
              <span className="text-muted-foreground">LLM Model:</span>
              <span className="ml-2 font-medium">
                {llmProvider === 'openai' 
                  ? (openaiModelId === 'gpt-5.2-2025-12-11' ? 'GPT-5.2' 
                    : openaiModelId === 'gpt-5-2025-08-07' ? 'GPT-5'
                    : openaiModelId === 'gpt-5-mini-2025-08-07' ? 'GPT-5 Mini'
                    : openaiModelId === 'gpt-4.1' ? 'GPT-4.1'
                    : openaiModelId === 'gpt-4o' ? 'GPT-4o'
                    : openaiModelId === 'gpt-4.1-mini' ? 'GPT-4.1 Mini'
                    : openaiModelId)
                  : (llmModels.find(m => m.id === llmModelId)?.name || 'No model selected')}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">{currentCloud.registryName}:</span>
              <span className="ml-2 font-medium">{ecrRegistryUrl || 'Not set'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Database:</span>
              <span className="ml-2 font-medium">
                {useExternalDatabase ? (postgresHost || 'External - Not configured') : 'Bundled (Helm chart)'}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Redis:</span>
              <span className="ml-2 font-medium">
                {useExternalDatabase ? (redisHost || 'External - Not configured') : 'Bundled (Helm chart)'}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Elasticsearch:</span>
              <span className="ml-2 font-medium">
                {elasticsearchEnabled ? (elasticsearchEndpoint || 'Not configured') : 'Disabled'}
              </span>
            </div>
          </div>
        </div>

        <Button 
          onClick={handleGenerateValues} 
          disabled={generating || !ecrRegistryUrl || (useExternalDatabase && (!postgresHost || !redisHost)) || (llmProvider === 'openai' && !openaiApiKey)}
          className="w-full"
        >
          {generating ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Zap className="w-4 h-4 mr-2" />
              Generate Helm Values
            </>
          )}
        </Button>

        {generatedValues && (
          <Tabs defaultValue="unified">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="unified">Unified</TabsTrigger>
              <TabsTrigger value="server">Server</TabsTrigger>
              <TabsTrigger value="agents">Agents</TabsTrigger>
              <TabsTrigger value="router">Router</TabsTrigger>
              <TabsTrigger value="frontend">Frontend</TabsTrigger>
            </TabsList>
            
            {(['unified', 'server', 'agents', 'router', 'frontend'] as const).map((tab) => (
              <TabsContent key={tab} value={tab}>
                <div className="relative">
                  <pre className="p-4 bg-slate-900 text-slate-100 rounded-lg overflow-auto max-h-96 text-xs">
                    {generatedValues[`${tab}_values` as keyof GenerateHelmValuesResponse]}
                  </pre>
                  <div className="absolute top-2 right-2 flex gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleCopyToClipboard(generatedValues[`${tab}_values` as keyof GenerateHelmValuesResponse] as string)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleDownload(generatedValues[`${tab}_values` as keyof GenerateHelmValuesResponse] as string, `kestrel-${tab}-values.yaml`)}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        )}

        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCurrentStep(4)}>
            Back
          </Button>
          <Button 
            onClick={() => handleStepComplete(5)} 
            disabled={!generatedValues}
            className="flex-1"
          >
            Continue <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderDownloadStep = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Download className="w-5 h-5" />
          Download & Deploy
        </CardTitle>
        <CardDescription>
          Download images and deploy Kestrel to your cluster.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {status?.latest_release && (
          <>
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertTitle className="text-green-800">Ready to Deploy</AlertTitle>
              <AlertDescription className="text-green-700">
                Helm Chart {status.latest_release.version} with Docker Images {status.latest_release.image_version || 'v1.0.0'} is ready for deployment.
              </AlertDescription>
            </Alert>

            <div className="space-y-4">
              <h4 className="font-medium">1. Docker Images</h4>
              
              <div className="p-4 bg-muted rounded-lg space-y-4">
                <div>
                  <p className="text-sm font-medium mb-2">Pull from Kestrel Registry & Push to Your ECR</p>
                  <p className="text-sm text-muted-foreground mb-4">
                    Pull images from our private registry (using credentials we provide), then push to your ECR. 
                    Your EKS cluster will pull images from your ECR during deployment.
                  </p>
                  
                  <Button 
                    className="w-full mb-4"
                    onClick={async () => {
                      try {
                        const creds = await apiClient.getOnPremPullCredentials();
                        setCredentialsData(creds);
                        setCredentialsModalOpen(true);
                      } catch (error) {
                        console.error('Failed to get pull credentials:', error);
                        setError('Failed to get pull credentials. Please contact support@usekestrel.ai');
                      }
                    }}
                  >
                    <Key className="w-4 h-4 mr-2" />
                    Get Registry Pull Credentials
                  </Button>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-3">Pull & Push Commands</p>
                  <p className="text-xs text-muted-foreground mb-3">
                    Run these commands from any machine with internet access (laptop, CI runner, etc.).
                  </p>
                  
                  {/* CLI / Console Tabs */}
                  <div className="border rounded-lg overflow-hidden">
                    <div className="flex border-b">
                      <button
                        className="flex-1 px-4 py-2 text-sm font-medium bg-background border-b-2 border-foreground text-foreground"
                        onClick={(e) => {
                          const parent = e.currentTarget.parentElement?.parentElement;
                          const cliTab = parent?.querySelector('[data-tab="cli"]');
                          const consoleTab = parent?.querySelector('[data-tab="console"]');
                          e.currentTarget.classList.add('border-foreground', 'text-foreground');
                          e.currentTarget.classList.remove('border-transparent', 'text-muted-foreground');
                          e.currentTarget.nextElementSibling?.classList.remove('border-foreground', 'text-foreground');
                          e.currentTarget.nextElementSibling?.classList.add('border-transparent', 'text-muted-foreground');
                          cliTab?.classList.remove('hidden');
                          consoleTab?.classList.add('hidden');
                        }}
                      >
                        CLI Commands
                      </button>
                      <button
                        className="flex-1 px-4 py-2 text-sm font-medium bg-background border-b-2 border-transparent text-muted-foreground"
                        onClick={(e) => {
                          const parent = e.currentTarget.parentElement?.parentElement;
                          const cliTab = parent?.querySelector('[data-tab="cli"]');
                          const consoleTab = parent?.querySelector('[data-tab="console"]');
                          e.currentTarget.classList.add('border-foreground', 'text-foreground');
                          e.currentTarget.classList.remove('border-transparent', 'text-muted-foreground');
                          e.currentTarget.previousElementSibling?.classList.remove('border-foreground', 'text-foreground');
                          e.currentTarget.previousElementSibling?.classList.add('border-transparent', 'text-muted-foreground');
                          consoleTab?.classList.remove('hidden');
                          cliTab?.classList.add('hidden');
                        }}
                      >
                        AWS Console
                      </button>
                    </div>
                    
                    {/* CLI Tab Content */}
                    <div data-tab="cli" className="p-4">
                      <pre className="p-3 bg-slate-900 text-slate-100 rounded text-xs overflow-auto">
{`# 1. Login to Kestrel registry (use credentials from button above)
echo $GHCR_TOKEN | docker login ghcr.io -u kestrel-customer --password-stdin

# 2. Login to your ECR
aws ecr get-login-password --region ${bedrockRegion || 'us-east-1'} | \\
  docker login --username AWS --password-stdin ${ecrRegistryUrl || '<your-ecr-url>'}

# 3. Create ECR repositories (if they don't exist)
for repo in server agents router frontend; do
  aws ecr create-repository --repository-name kestrel/$repo \\
    --region ${bedrockRegion || 'us-east-1'} 2>/dev/null || true
done

# 4. Pull, tag, and push each image
VERSION="${status.latest_release.version}"
ECR="${ecrRegistryUrl || '<your-ecr-url>'}"
GHCR="ghcr.io/kestrelai/autonp"

for img in server agents router frontend; do
  docker pull $GHCR/onprem-$img:$VERSION
  docker tag $GHCR/onprem-$img:$VERSION $ECR/kestrel/$img:$VERSION
  docker push $ECR/kestrel/$img:$VERSION
done

echo "✓ Images pushed to $ECR/kestrel/"`}
                      </pre>
                    </div>
                    
                    {/* Console Tab Content */}
                    <div data-tab="console" className="p-4 hidden">
                      <div className="space-y-4 text-sm">
                        <div>
                          <p className="font-medium mb-1">Step 1: Open ECR Console</p>
                          <p className="text-muted-foreground">
                            Go to <a href={`https://${bedrockRegion || 'us-east-1'}.console.aws.amazon.com/ecr/repositories`} target="_blank" rel="noopener noreferrer" className="text-foreground underline">
                              Amazon ECR → Repositories
                            </a>
                          </p>
                        </div>
                        
                        <div>
                          <p className="font-medium mb-1">Step 2: Create Repositories</p>
                          <p className="text-muted-foreground mb-2">
                            Click <strong>"Create repository"</strong> and create these 4 repositories:
                          </p>
                          <ul className="text-muted-foreground list-disc list-inside space-y-1 ml-2">
                            <li><code className="bg-background px-1 rounded">kestrel/server</code></li>
                            <li><code className="bg-background px-1 rounded">kestrel/agents</code></li>
                            <li><code className="bg-background px-1 rounded">kestrel/router</code></li>
                            <li><code className="bg-background px-1 rounded">kestrel/frontend</code></li>
                          </ul>
                          <p className="text-muted-foreground mt-2">
                            For each: Visibility = <strong>Private</strong>, leave other settings as default.
                          </p>
                        </div>
                        
                        <div>
                          <p className="font-medium mb-1">Step 3: Push Images</p>
                          <p className="text-muted-foreground mb-2">
                            After creating each repository, click on it and select <strong>"View push commands"</strong> for the auth command. Then pull from Kestrel registry and push:
                          </p>
                          <pre className="p-2 bg-slate-900 text-slate-100 rounded text-xs overflow-auto">
{`# Pull, tag, and push all images
VERSION="${status?.latest_release?.version || 'v1.0.0'}"
ECR="${ecrRegistryUrl || '<your-ecr-url>'}"
GHCR="ghcr.io/kestrelai/autonp"

for img in server agents router frontend; do
  docker pull $GHCR/onprem-$img:$VERSION
  docker tag $GHCR/onprem-$img:$VERSION $ECR/kestrel/$img:$VERSION
  docker push $ECR/kestrel/$img:$VERSION
done`}
                          </pre>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {deploymentType === 'airgapped' && (
                  <Alert className="bg-amber-50 border-amber-200">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <AlertTitle className="text-amber-800">Air-Gapped: VPC Endpoint Checklist</AlertTitle>
                    <AlertDescription className="text-amber-700 text-sm">
                      Verify all required VPC endpoints are created:
                      <ul className="list-none mt-2 space-y-1">
                        <li>✓ <code className="bg-amber-100 px-1 rounded">bedrock-runtime</code> - Created in LLM Configuration step</li>
                        <li>✓ <code className="bg-amber-100 px-1 rounded">ecr.api</code>, <code className="bg-amber-100 px-1 rounded">ecr.dkr</code>, <code className="bg-amber-100 px-1 rounded">s3</code> - Created in Infrastructure step</li>
                        <li className="font-medium">→ <code className="bg-amber-100 px-1 rounded">com.amazonaws.{bedrockRegion || 'us-east-1'}.secretsmanager</code> - <strong>Create now</strong> (for runtime secrets)</li>
                      </ul>
                      <p className="mt-2">
                        <a 
                          href={`https://${bedrockRegion || 'us-east-1'}.console.aws.amazon.com/vpc/home?region=${bedrockRegion || 'us-east-1'}#Endpoints:`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-amber-800 underline font-medium"
                        >
                          Open VPC Endpoints Console →
                        </a>
                      </p>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-medium">2. Create Kubernetes Resources & Secrets</h4>
              <p className="text-sm text-muted-foreground mb-3">
                Kestrel uses AWS Secrets Manager to securely store sensitive configuration. Create the Kubernetes namespace and required secrets.
              </p>
              
              {deploymentType === 'airgapped' && (
                <Alert className="bg-muted/50 border-border mb-4">
                  <Info className="h-4 w-4 text-foreground" />
                  <AlertTitle className="text-foreground">Air-Gapped: Secrets Manager Access</AlertTitle>
                  <AlertDescription className="text-muted-foreground text-sm">
                    <p className="mb-2">
                      For Kestrel to read secrets at runtime, your EKS cluster needs access to AWS Secrets Manager via VPC endpoint.
                    </p>
                    <p className="font-medium">Setup VPC Endpoint:</p>
                    <ol className="list-decimal list-inside mt-1 space-y-1 text-xs">
                      <li>Go to <a href={`https://${bedrockRegion || 'us-east-1'}.console.aws.amazon.com/vpc/home?region=${bedrockRegion || 'us-east-1'}#Endpoints:`} target="_blank" rel="noopener noreferrer" className="text-foreground underline">VPC → Endpoints</a></li>
                      <li>Click <strong>Create endpoint</strong></li>
                      <li>Service name: <code className="bg-muted px-1 rounded">com.amazonaws.{bedrockRegion || 'us-east-1'}.secretsmanager</code></li>
                      <li>VPC: Select your EKS cluster's VPC</li>
                      <li>Subnets: Select private subnets where EKS nodes run</li>
                      <li>Security group: Allow inbound HTTPS (443) from EKS nodes</li>
                      <li>Enable <strong>Private DNS</strong> so services can use the standard endpoint</li>
                    </ol>
                    <p className="mt-2 text-xs">
                      The <code className="bg-muted px-1 rounded">KestrelServicePolicy</code> you created in the Infrastructure step already includes <code className="bg-muted px-1 rounded">secretsmanager:GetSecretValue</code> permission.
                    </p>
                  </AlertDescription>
                </Alert>
              )}
              
              {/* CLI / Console Tabs for Secrets */}
              <div className="border rounded-lg overflow-hidden">
                <div className="flex border-b">
                  <button
                    className="flex-1 px-4 py-2 text-sm font-medium bg-background border-b-2 border-foreground text-foreground"
                    onClick={(e) => {
                      const parent = e.currentTarget.parentElement?.parentElement;
                      const cliTab = parent?.querySelector('[data-tab="secrets-cli"]');
                      const consoleTab = parent?.querySelector('[data-tab="secrets-console"]');
                      e.currentTarget.classList.add('border-foreground', 'text-foreground');
                      e.currentTarget.classList.remove('border-transparent', 'text-muted-foreground');
                      e.currentTarget.nextElementSibling?.classList.remove('border-foreground', 'text-foreground');
                      e.currentTarget.nextElementSibling?.classList.add('border-transparent', 'text-muted-foreground');
                      cliTab?.classList.remove('hidden');
                      consoleTab?.classList.add('hidden');
                    }}
                  >
                    CLI Commands
                  </button>
                  <button
                    className="flex-1 px-4 py-2 text-sm font-medium bg-background border-b-2 border-transparent text-muted-foreground"
                    onClick={(e) => {
                      const parent = e.currentTarget.parentElement?.parentElement;
                      const cliTab = parent?.querySelector('[data-tab="secrets-cli"]');
                      const consoleTab = parent?.querySelector('[data-tab="secrets-console"]');
                      e.currentTarget.classList.add('border-foreground', 'text-foreground');
                      e.currentTarget.classList.remove('border-transparent', 'text-muted-foreground');
                      e.currentTarget.previousElementSibling?.classList.remove('border-foreground', 'text-foreground');
                      e.currentTarget.previousElementSibling?.classList.add('border-transparent', 'text-muted-foreground');
                      consoleTab?.classList.remove('hidden');
                      cliTab?.classList.add('hidden');
                    }}
                  >
                    AWS Console
                  </button>
                </div>
                
                {/* CLI Tab Content */}
                <div data-tab="secrets-cli" className="p-4">
                  <pre className="p-3 bg-slate-900 text-slate-100 rounded text-xs overflow-auto">
{`# 1. Create Kubernetes namespace
kubectl create namespace kestrel

# 2. Create ECR pull secret (allows cluster to pull images from your ECR)
kubectl create secret docker-registry ecr-secret \\
  --docker-server=${ecrRegistryUrl || '<your-ecr-url>'} \\
  --docker-username=AWS \\
  --docker-password=$(aws ecr get-login-password --region ${bedrockRegion || 'us-east-1'}) \\
  -n kestrel

# 3. Create secrets in AWS Secrets Manager
# These secrets are read by Kestrel services at runtime

# Required: Database password
aws secretsmanager create-secret \\
  --name kestrel/postgres-password \\
  --secret-string "$(openssl rand -base64 24)" \\
  --region ${bedrockRegion || 'us-east-1'}

# Required: JWT signing key (for authentication)
aws secretsmanager create-secret \\
  --name kestrel/jwt-sign-key \\
  --secret-string "$(openssl rand -base64 32)" \\
  --region ${bedrockRegion || 'us-east-1'}

# Optional: Email notifications (Resend API key)
# aws secretsmanager create-secret \\
#   --name kestrel/resend-api-key \\
#   --secret-string "re_your_api_key" \\
#   --region ${bedrockRegion || 'us-east-1'}`}
                  </pre>
                </div>
                
                {/* Console Tab Content */}
                <div data-tab="secrets-console" className="p-4 hidden">
                  <div className="space-y-4 text-sm">
                    <div>
                      <p className="font-medium mb-1">Step 1: Create Kubernetes Namespace</p>
                      <p className="text-muted-foreground mb-2">
                        In your terminal or EKS console, create the <code className="bg-muted px-1 rounded">kestrel</code> namespace:
                      </p>
                      <pre className="p-2 bg-slate-900 text-slate-100 rounded text-xs overflow-auto">
{`kubectl create namespace kestrel`}
                      </pre>
                    </div>
                    
                    <div>
                      <p className="font-medium mb-1">Step 2: Open AWS Secrets Manager Console</p>
                      <p className="text-muted-foreground">
                        Go to <a href={`https://${bedrockRegion || 'us-east-1'}.console.aws.amazon.com/secretsmanager/listsecrets`} target="_blank" rel="noopener noreferrer" className="text-foreground underline">
                          AWS Secrets Manager → Secrets
                        </a>
                      </p>
                    </div>
                    
                    <div>
                      <p className="font-medium mb-1">Step 3: Create Required Secrets</p>
                      <p className="text-muted-foreground mb-2">
                        Click <strong>"Store a new secret"</strong> and create these secrets:
                      </p>
                      
                      <div className="space-y-3 ml-2">
                        <div className="p-3 bg-muted rounded">
                          <p className="font-medium text-xs mb-1">Secret 1: Database Password</p>
                          <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
                            <li>Secret type: <strong>Other type of secret</strong></li>
                            <li>Key/value: Choose <strong>Plaintext</strong> tab, enter a secure password</li>
                            <li>Secret name: <code className="bg-background px-1 rounded">kestrel/postgres-password</code></li>
                          </ul>
                        </div>
                        
                        <div className="p-3 bg-muted rounded">
                          <p className="font-medium text-xs mb-1">Secret 2: JWT Signing Key</p>
                          <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
                            <li>Secret type: <strong>Other type of secret</strong></li>
                            <li>Key/value: Choose <strong>Plaintext</strong> tab, enter a 32+ character random string</li>
                            <li>Secret name: <code className="bg-background px-1 rounded">kestrel/jwt-sign-key</code></li>
                          </ul>
                          <p className="text-xs text-muted-foreground mt-1">
                            Generate with: <code className="bg-background px-1 rounded">openssl rand -base64 32</code>
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <p className="font-medium mb-1">Step 4: Create ECR Pull Secret</p>
                      <p className="text-muted-foreground mb-2">
                        Run this command to allow your cluster to pull images from ECR:
                      </p>
                      <pre className="p-2 bg-slate-900 text-slate-100 rounded text-xs overflow-auto">
{`kubectl create secret docker-registry ecr-secret \\
  --docker-server=${ecrRegistryUrl || '<your-ecr-url>'} \\
  --docker-username=AWS \\
  --docker-password=$(aws ecr get-login-password --region ${bedrockRegion || 'us-east-1'}) \\
  -n kestrel`}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>
              
              <p className="text-xs text-muted-foreground mt-2">
                <strong>Note:</strong> The <code className="bg-muted px-1 rounded">KestrelServicePolicy</code> you created in the Infrastructure step already includes <code className="bg-muted px-1 rounded">secretsmanager:GetSecretValue</code> permission for secrets with prefix <code className="bg-muted px-1 rounded">kestrel/*</code>.
              </p>
            </div>

            {/* Pre-deployment Prerequisites */}
            <Alert className="bg-muted/50 border-border mb-4">
              <Info className="h-4 w-4 text-muted-foreground" />
              <AlertTitle className="text-foreground">Pre-deployment Requirements</AlertTitle>
              <AlertDescription className="text-muted-foreground text-sm">
                <ul className="list-disc list-inside space-y-1 mt-2">
                  <li>
                    <strong>EBS CSI Driver:</strong> Required for persistent volume provisioning on EKS.
                    <br />
                    Check if installed: <code className="bg-muted px-1 rounded text-xs">kubectl get pods -n kube-system | grep ebs</code>
                    <br />
                    If not installed: <code className="bg-muted px-1 rounded text-xs">aws eks create-addon --cluster-name YOUR_CLUSTER --addon-name aws-ebs-csi-driver --region YOUR_REGION</code>
                  </li>
                  <li>
                    <strong>Default Storage Class:</strong> Ensure your cluster has a default storage class for persistent volumes.
                    <br />
                    <code className="bg-muted px-1 rounded text-xs">kubectl get storageclass</code> — look for <code className="bg-muted px-1 rounded text-xs">(default)</code>
                    <br />
                    If no default exists: <code className="bg-muted px-1 rounded text-xs">kubectl patch storageclass gp2 -p '{`'{"metadata": {"annotations":{"storageclass.kubernetes.io/is-default-class":"true"}}}'`}'</code>
                  </li>
                  {deploymentType === 'airgapped' && (
                    <li>
                      <strong>VPC Endpoint Security Groups:</strong> Ensure all VPC endpoint security groups allow inbound HTTPS (443) from your EKS nodes' CIDR range.
                    </li>
                  )}
                </ul>
              </AlertDescription>
            </Alert>

            <div className="space-y-4">
              <h4 className="font-medium">3. Deploy with Helm</h4>
              {generatedValues && (
                <div className="flex flex-wrap gap-2 mb-3">
                  <Button
                    variant="outline"
                    onClick={() => handleDownload(generatedValues.unified_values, 'kestrel-values.yaml')}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download values.yaml
                  </Button>
                  <Button
                    variant="outline"
                    onClick={async () => {
                      const version = status?.latest_release?.version || 'v1.0.0';
                      try {
                        await apiClient.downloadOnPremHelmChart(version);
                        toast.success('Helm chart downloaded successfully');
                      } catch (error: any) {
                        console.error('Failed to download Helm chart:', error);
                        const message = error?.response?.status === 404
                          ? 'Helm chart not found. Please contact support.'
                          : 'Failed to download Helm chart. Please try again.';
                        toast.error(message);
                      }
                    }}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Helm Chart
                  </Button>
                </div>
              )}
              
              {deploymentType === 'airgapped' ? (
                <div className="space-y-3">
                  <Alert className="bg-muted/50 border-border">
                    <Info className="h-4 w-4 text-foreground" />
                    <AlertTitle className="text-foreground">Air-Gapped Deployment</AlertTitle>
                    <AlertDescription className="text-muted-foreground text-sm">
                      Download both the <strong>values.yaml</strong> and <strong>Helm Chart</strong> above, then transfer them to your air-gapped environment.
                    </AlertDescription>
                  </Alert>
                  <pre className="p-3 bg-slate-900 text-slate-100 rounded text-xs overflow-auto">
{`# Transfer kestrel-values.yaml and kestrel-${(status?.latest_release?.version || 'v1.0.0').replace('v', '')}.tgz to your air-gapped environment

# Install Kestrel from local chart (no need to extract - helm can install directly from .tgz)
helm install kestrel kestrel-${(status?.latest_release?.version || 'v1.0.0').replace('v', '')}.tgz \\
  -f kestrel-values.yaml \\
  -n kestrel`}
                  </pre>
                </div>
              ) : (
                <pre className="p-3 bg-slate-900 text-slate-100 rounded text-xs overflow-auto">
{`# Install using OCI registry (recommended)
helm install kestrel oci://ghcr.io/kestrelai/charts/kestrel \\
  --version ${(status?.latest_release?.version || 'v1.0.0').replace(/^v/, '')} \\
  -f kestrel-values.yaml \\
  -n kestrel --create-namespace

# Or using Helm repository
helm repo add kestrel https://charts.usekestrel.ai
helm repo update
helm install kestrel kestrel/kestrel \\
  -f kestrel-values.yaml \\
  -n kestrel --create-namespace`}
                </pre>
              )}
            </div>

            <div className="space-y-4">
              <h4 className="font-medium">4. Verify Deployment</h4>
              <pre className="p-3 bg-slate-900 text-slate-100 rounded text-xs overflow-auto">
{`# Check pod status
kubectl get pods -n kestrel

# Check logs
kubectl logs -f deployment/kestrel-autonp-server -n kestrel

# Access the UI (temporary, for testing)
kubectl port-forward svc/kestrel-autonp-frontend 3000:80 -n kestrel
# Then open http://localhost:3000`}
              </pre>
            </div>

            <div className="space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                5. Configure DNS for Ingress
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" className="text-muted-foreground hover:text-foreground">
                      <Info className="w-4 h-4" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[28rem]" align="start">
                    <div className="space-y-2 py-2 text-sm">
                      <p className="font-medium">Why is DNS Configuration Needed?</p>
                      <p className="text-muted-foreground">
                        DNS maps your chosen hostname (e.g., <code className="bg-muted px-1 rounded">kestrel.yourcompany.com</code>) 
                        to the load balancer created by Kubernetes. This enables:
                      </p>
                      <ul className="list-disc list-inside text-muted-foreground space-y-1">
                        <li>Browser access to the Kestrel dashboard</li>
                        <li>Slack/GitHub webhook callbacks (if using integrations)</li>
                        <li>API access for external integrations</li>
                      </ul>
                      <p className="text-muted-foreground text-xs mt-2">
                        <strong>Note:</strong> Kestrel Operators use separate connectivity (internal service, NLB, or PrivateLink) 
                        configured during cluster onboarding, not this external hostname.
                      </p>
                    </div>
                  </PopoverContent>
                </Popover>
              </h4>
              <p className="text-sm text-muted-foreground">
                Now that Kestrel is deployed, configure DNS to point your hostname (<code className="bg-muted px-1 rounded">{ingressHost || 'kestrel.yourcompany.com'}</code>) to the ingress.
              </p>
              
              <div className="space-y-3">
                <p className="text-sm font-medium">Step A: Get the gRPC Ingress Address</p>
                <pre className="p-3 bg-slate-900 text-slate-100 rounded text-xs overflow-auto">
{`# Get the gRPC ingress address (used by Kestrel Operators to connect)
kubectl get ingress -n kestrel

# Example output:
# NAME                          CLASS   HOSTS                           ADDRESS                                      PORTS
# kestrel-autonp-server-grpc    nginx   ${ingressHost || 'kestrel.yourcompany.com'}   a1b2c3d4e5.us-west-1.elb.amazonaws.com       80, 443`}
                </pre>
                <p className="text-xs text-muted-foreground">
                  Copy the <strong>ADDRESS</strong> value - this is the load balancer endpoint for operator gRPC connections.
                </p>
              </div>

              <div className="space-y-3">
                <p className="text-sm font-medium">Step B: Create DNS Record in Route 53</p>
                <div className="p-4 bg-muted rounded-lg space-y-3">
                  <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-2">
                    <li>
                      Open <a href="https://console.aws.amazon.com/route53/v2/hostedzones" target="_blank" rel="noopener noreferrer" className="text-foreground underline">
                        AWS Route 53 Console → Hosted Zones
                      </a>
                    </li>
                    <li>Select your hosted zone (e.g., <code className="bg-background px-1 rounded">yourcompany.com</code>)</li>
                    <li>Click <strong>"Create record"</strong></li>
                    <li>
                      Configure the record:
                      <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                        <li><strong>Record name:</strong> <code className="bg-background px-1 rounded">{ingressHost?.split('.')[0] || 'kestrel'}</code></li>
                        <li><strong>Record type:</strong> <code className="bg-background px-1 rounded">CNAME</code> (or <code className="bg-background px-1 rounded">A - Alias</code> for ALB)</li>
                        <li><strong>Value:</strong> Paste the ADDRESS from Step A</li>
                        <li><strong>TTL:</strong> 300 (5 minutes)</li>
                      </ul>
                    </li>
                    <li>Click <strong>"Create records"</strong></li>
                  </ol>
                </div>
              </div>

              {deploymentType === 'airgapped' && (
                <div className="space-y-4">
                  <Alert className="bg-amber-50 border-amber-200">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <AlertTitle className="text-amber-800">Air-Gapped: Use Private Hosted Zone</AlertTitle>
                    <AlertDescription className="text-amber-700 text-sm">
                      For air-gapped deployments, use a <strong>Route 53 Private Hosted Zone</strong> associated with your VPC. 
                      This ensures DNS resolution works within your private network without internet access.
                    </AlertDescription>
                  </Alert>

                  <div className="border rounded-lg p-4 space-y-4">
                    <h5 className="font-medium text-sm">Multi-VPC Kestrel Operator Connectivity via AWS PrivateLink</h5>
                    <p className="text-sm text-muted-foreground">
                      If Kestrel Operators run in different VPCs (e.g., customer clusters in separate accounts/VPCs), 
                      use AWS PrivateLink to enable secure, private connectivity without internet exposure.
                    </p>

                    <div className="space-y-3">
                      <p className="text-sm font-medium">Step 1: Get the Internal NLB DNS</p>
                      <p className="text-xs text-muted-foreground mb-2">
                        The gRPC service is deployed with an <strong>internal Network Load Balancer (NLB)</strong> which is required for PrivateLink.
                        Internal NLBs are not accessible from the internet, only from within the VPC or via VPC peering/PrivateLink.
                      </p>
                      <pre className="p-3 bg-slate-900 text-slate-100 rounded text-xs overflow-auto">
{`# Get the gRPC LoadBalancer service (this is the internal NLB for PrivateLink)
kubectl get svc -n kestrel | grep grpc

# Example output:
# kestrel-autonp-server-grpc   LoadBalancer   10.100.x.x   abc123def456-xyz789.elb.us-west-1.amazonaws.com   50051:31234/TCP,443:31235/TCP

# To verify it's an internal NLB, check in AWS Console or use CLI:
aws elbv2 describe-load-balancers --query 'LoadBalancers[*].{Name:LoadBalancerName,Type:Type,Scheme:Scheme}' --output table

# Should show Type: "network" and Scheme: "internal"
# The NLB hostname is used for the VPC Endpoint Service`}
                      </pre>
                    </div>

                    <div className="space-y-3">
                      <p className="text-sm font-medium">Step 2: Create VPC Endpoint Service (Server VPC)</p>
                      <div className="p-4 bg-muted rounded-lg space-y-3">
                        <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-2">
                          <li>
                            Open <a href="https://console.aws.amazon.com/vpc/home#Endpoints:" target="_blank" rel="noopener noreferrer" className="text-foreground underline">
                              AWS VPC Console → Endpoint Services
                            </a>
                          </li>
                          <li>Click <strong>"Create endpoint service"</strong></li>
                          <li>
                            Configure:
                            <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                              <li><strong>Load balancer type:</strong> Network</li>
                              <li><strong>Available load balancers:</strong> Select the NLB created by the gRPC service</li>
                              <li><strong>Require acceptance:</strong> Enable (recommended for security)</li>
                            </ul>
                          </li>
                          <li>Click <strong>"Create"</strong> and note the <strong>Service name</strong> (e.g., <code className="bg-background px-1 rounded">com.amazonaws.vpce.us-west-1.vpce-svc-xxxxx</code>)</li>
                          <li>
                            Under <strong>"Allow principals"</strong>, add the AWS account IDs that will connect:
                            <code className="block bg-background px-2 py-1 rounded mt-1 text-xs">arn:aws:iam::ACCOUNT_ID:root</code>
                          </li>
                        </ol>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <p className="text-sm font-medium">Step 3: Create VPC Interface Endpoint (Kestrel Operator VPC)</p>
                      <div className="p-4 bg-muted rounded-lg space-y-3">
                        <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-2">
                          <li>In each VPC where operators will run, open <strong>VPC Console → Endpoints</strong></li>
                          <li>Click <strong>"Create endpoint"</strong></li>
                          <li>
                            Configure:
                            <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                              <li><strong>Service category:</strong> Find service by name</li>
                              <li><strong>Service name:</strong> Paste the endpoint service name from Step 2</li>
                              <li><strong>VPC:</strong> Select the VPC where operators run</li>
                              <li><strong>Subnets:</strong> Select subnets in each AZ</li>
                              <li><strong>Security groups:</strong> Allow inbound TCP on port 443 (gRPC with TLS)</li>
                              <li><strong>Enable DNS name:</strong> Check this option</li>
                            </ul>
                          </li>
                          <li>Click <strong>"Create endpoint"</strong></li>
                          <li>Accept the endpoint connection in the server VPC's endpoint service console</li>
                        </ol>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <p className="text-sm font-medium">Step 4: Configure Kestrel Operators to Connect</p>
                      <pre className="p-3 bg-slate-900 text-slate-100 rounded text-xs overflow-auto">
{`# Get the VPC Endpoint DNS name
aws ec2 describe-vpc-endpoints --filters "Name=service-name,Values=com.amazonaws.vpce.us-west-1.vpce-svc-xxxxx" \\
  --query "VpcEndpoints[0].DnsEntries[0].DnsName" --output text

# Example output:
# vpce-xxxxx-yyyyy.vpce-svc-xxxxx.us-west-1.vpce.amazonaws.com

# Configure the Kestrel Operator to use this endpoint:
# During cluster onboarding, select "Different VPC" and enter this endpoint as the Server Host.
# The generated values.yaml will include:
# server:
#   host: "vpce-xxxxx-yyyyy.vpce-svc-xxxxx.us-west-1.vpce.amazonaws.com"
#   port: 443
#   tls:
#     insecureSkipVerify: true`}
                      </pre>
                    </div>

                    <Alert className="bg-green-50 border-green-200">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <AlertTitle className="text-green-800">Same VPC Kestrel Operators</AlertTitle>
                      <AlertDescription className="text-green-700 text-sm">
                        Kestrel Operators in the <strong>same VPC</strong> as the Kestrel server can connect directly without PrivateLink:
                        <ul className="list-disc list-inside mt-1 space-y-0.5">
                          <li><strong>Same cluster:</strong> Internal K8s service DNS (<code className="bg-green-100 px-1 rounded">kestrel-autonp-server-grpc.kestrel.svc.cluster.local</code>)</li>
                          <li><strong>Different cluster:</strong> NLB DNS (from <code className="bg-green-100 px-1 rounded">kubectl get svc -n kestrel | grep grpc</code>)</li>
                        </ul>
                        <span className="text-xs">Configure during cluster onboarding in the Kestrel UI.</span>
                      </AlertDescription>
                    </Alert>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <p className="text-sm font-medium">Step C: Verify DNS Resolution</p>
                <pre className="p-3 bg-slate-900 text-slate-100 rounded text-xs overflow-auto">
{`# Wait a few minutes for DNS propagation, then verify
nslookup ${ingressHost || 'kestrel.yourcompany.com'}

# Or use dig
dig ${ingressHost || 'kestrel.yourcompany.com'}

# Test access (may need to wait for TLS cert if using HTTPS)
curl -I https://${ingressHost || 'kestrel.yourcompany.com'}`}
                </pre>
              </div>

              <Alert className="bg-muted/50 border-border">
                <Info className="h-4 w-4 text-muted-foreground" />
                <AlertTitle className="text-foreground">Alternative DNS Providers</AlertTitle>
                <AlertDescription className="text-muted-foreground text-sm">
                  If you don't use Route 53, create a <strong>CNAME record</strong> in your DNS provider 
                  (Cloudflare, GoDaddy, etc.) pointing <code className="bg-muted px-1 rounded">{ingressHost || 'kestrel.yourcompany.com'}</code> to 
                  the ADDRESS from Step A.
                </AlertDescription>
              </Alert>
            </div>

            {/* Slack Integration - only for non-airgapped deployments */}
            {deploymentType !== 'airgapped' && (
              <div className="space-y-6">
                {/* Slack Integration */}
                <div className="space-y-4">
                  <h4 className="font-medium flex items-center gap-2">
                    6. Slack Integration (Optional)
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" className="text-muted-foreground hover:text-foreground">
                          <Info className="w-4 h-4" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[28rem]" align="start">
                        <div className="space-y-2 py-2 text-sm">
                          <p className="font-medium">Why Create Your Own Slack App?</p>
                          <p className="text-muted-foreground">
                            Slack requires OAuth redirect URLs to be pre-registered in the Slack App configuration. 
                            Since on-prem deployments have different URLs than Kestrel's SaaS platform, 
                            you need to create your own Slack App to enable the integration.
                          </p>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Enable Slack integration to receive incident notifications and use the Kestrel Slack bot for Kubernetes assistance.
                  </p>

                  <div className="border rounded-lg p-4 space-y-4">
                    <div className="space-y-3">
                      <p className="text-sm font-medium">Step 1: Create a Slack App</p>
                      <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-2">
                        <li>
                          Go to <a href="https://api.slack.com/apps" target="_blank" rel="noopener noreferrer" className="text-foreground underline">
                            api.slack.com/apps
                          </a> and click <strong>"Create New App"</strong>
                        </li>
                        <li>Choose <strong>"From scratch"</strong>, name it (e.g., "Kestrel"), and select your workspace</li>
                        <li>
                          Under <strong>"OAuth & Permissions"</strong>, add these <strong>Bot Token Scopes</strong>:
                          <ul className="list-disc list-inside ml-4 mt-1 space-y-0.5 text-xs">
                            <li><code className="bg-muted px-1 rounded">channels:read</code></li>
                            <li><code className="bg-muted px-1 rounded">chat:write</code></li>
                            <li><code className="bg-muted px-1 rounded">chat:write.public</code></li>
                            <li><code className="bg-muted px-1 rounded">users:read</code></li>
                            <li><code className="bg-muted px-1 rounded">users:read.email</code></li>
                            <li><code className="bg-muted px-1 rounded">im:write</code></li>
                            <li><code className="bg-muted px-1 rounded">im:history</code></li>
                            <li><code className="bg-muted px-1 rounded">app_mentions:read</code></li>
                            <li><code className="bg-muted px-1 rounded">commands</code></li>
                          </ul>
                        </li>
                        <li>
                          Add this <strong>User Token Scope</strong>:
                          <ul className="list-disc list-inside ml-4 mt-1 text-xs">
                            <li><code className="bg-muted px-1 rounded">search:read</code></li>
                          </ul>
                        </li>
                        <li>
                          Under <strong>"Redirect URLs"</strong>, add:
                          <code className="block bg-muted px-2 py-1 rounded mt-1 text-xs">https://{ingressHost || 'kestrel.yourcompany.com'}/slack/callback</code>
                        </li>
                      </ol>
                    </div>

                    <div className="space-y-3">
                      <p className="text-sm font-medium">Step 2: Get Credentials</p>
                      <p className="text-sm text-muted-foreground">
                        From your Slack App's <strong>"Basic Information"</strong> page, copy:
                      </p>
                      <ul className="list-disc list-inside text-sm text-muted-foreground ml-2">
                        <li><strong>Client ID</strong></li>
                        <li><strong>Client Secret</strong></li>
                        <li><strong>Signing Secret</strong></li>
                      </ul>
                    </div>

                    <div className="space-y-3">
                      <p className="text-sm font-medium">Step 3: Store Credentials in AWS Secrets Manager</p>
                      <p className="text-sm text-muted-foreground mb-2">
                        Store each credential as a separate secret in AWS Secrets Manager (using the same <code className="bg-muted px-1 rounded">kestrel/</code> prefix as your other secrets):
                      </p>
                      <pre className="p-3 bg-slate-900 text-slate-100 rounded text-xs overflow-auto">
{`# Store Slack credentials in AWS Secrets Manager
aws secretsmanager create-secret \\
  --name kestrel/slack-client-id \\
  --secret-string "your-client-id" \\
  --region ${bedrockRegion || 'us-west-1'}

aws secretsmanager create-secret \\
  --name kestrel/slack-client-secret \\
  --secret-string "your-client-secret" \\
  --region ${bedrockRegion || 'us-west-1'}

aws secretsmanager create-secret \\
  --name kestrel/slack-signing-secret \\
  --secret-string "your-signing-secret" \\
  --region ${bedrockRegion || 'us-west-1'}`}
                      </pre>
                      <p className="text-xs text-muted-foreground mt-2">
                        The server will automatically read these secrets using the same AWS Secrets Manager configuration set up in Step 2.
                      </p>
                    </div>

                    <div className="space-y-3">
                      <p className="text-sm font-medium">Step 4: Enable Event Subscriptions</p>
                      <p className="text-sm text-muted-foreground">
                        For the Slack bot to respond to messages and commands:
                      </p>
                      <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
                        <li>In your Slack App, go to <strong>"Event Subscriptions"</strong></li>
                        <li>Enable events and set Request URL to: <code className="bg-muted px-1 rounded text-xs">https://{ingressHost || 'kestrel.yourcompany.com'}/api/integrations/slack/events</code></li>
                        <li>Subscribe to bot events: <code className="bg-muted px-1 rounded text-xs">app_mention</code>, <code className="bg-muted px-1 rounded text-xs">message.im</code></li>
                        <li>Go to <strong>"Slash Commands"</strong> and create <code className="bg-muted px-1 rounded text-xs">/kestrel</code> pointing to: <code className="bg-muted px-1 rounded text-xs">https://{ingressHost || 'kestrel.yourcompany.com'}/api/integrations/slack/commands</code></li>
                      </ol>
                    </div>

                    <Alert className="bg-amber-50 border-amber-200">
                      <AlertCircle className="h-4 w-4 text-amber-600" />
                      <AlertTitle className="text-amber-800">Requires Internet Access</AlertTitle>
                      <AlertDescription className="text-amber-700 text-sm">
                        Slack integration requires outbound internet access to <code className="bg-amber-100 px-1 rounded">slack.com</code> and <code className="bg-amber-100 px-1 rounded">api.slack.com</code>. 
                        This is not available in air-gapped deployments.
                      </AlertDescription>
                    </Alert>
                  </div>
                </div>

                {/* GitHub Integration */}
                <div className="space-y-4">
                  <h4 className="font-medium flex items-center gap-2">
                    7. GitHub Integration (Optional)
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" className="text-muted-foreground hover:text-foreground">
                          <Info className="w-4 h-4" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[28rem]" align="start">
                        <div className="space-y-2 py-2 text-sm">
                          <p className="font-medium">Why Create Your Own GitHub App?</p>
                          <p className="text-muted-foreground">
                            GitHub Apps send webhook events to a pre-configured URL. Since on-prem deployments 
                            have different URLs than Kestrel's SaaS platform, you need to create your own 
                            GitHub App with webhooks pointing to your server.
                          </p>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Enable GitHub integration for IaC scanning, automated PR creation for network policies, and CI/CD integration.
                  </p>

                  <div className="border rounded-lg p-4 space-y-4">
                    <div className="space-y-3">
                      <p className="text-sm font-medium">Step 1: Create a GitHub App</p>
                      <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-2">
                        <li>
                          Go to <a href="https://github.com/settings/apps/new" target="_blank" rel="noopener noreferrer" className="text-foreground underline">
                            GitHub Settings → Developer Settings → GitHub Apps → New GitHub App
                          </a>
                        </li>
                        <li>
                          Configure the app:
                          <ul className="list-disc list-inside ml-4 mt-1 space-y-0.5 text-xs">
                            <li><strong>GitHub App name:</strong> e.g., "Kestrel-YourCompany"</li>
                            <li><strong>Homepage URL:</strong> <code className="bg-muted px-1 rounded">https://{ingressHost || 'kestrel.yourcompany.com'}</code></li>
                            <li><strong>Webhook URL:</strong> <code className="bg-muted px-1 rounded">https://{ingressHost || 'kestrel.yourcompany.com'}/api/webhooks/github</code></li>
                            <li><strong>Webhook secret:</strong> Generate a secure random string</li>
                          </ul>
                        </li>
                        <li>
                          Set <strong>Repository permissions</strong>:
                          <ul className="list-disc list-inside ml-4 mt-1 space-y-0.5 text-xs">
                            <li><code className="bg-muted px-1 rounded">Contents</code>: Read & write</li>
                            <li><code className="bg-muted px-1 rounded">Pull requests</code>: Read & write</li>
                            <li><code className="bg-muted px-1 rounded">Metadata</code>: Read-only</li>
                          </ul>
                        </li>
                        <li>
                          Subscribe to <strong>events</strong>:
                          <ul className="list-disc list-inside ml-4 mt-1 text-xs">
                            <li><code className="bg-muted px-1 rounded">Installation</code></li>
                            <li><code className="bg-muted px-1 rounded">Push</code></li>
                            <li><code className="bg-muted px-1 rounded">Pull request</code></li>
                          </ul>
                        </li>
                        <li>Click <strong>"Create GitHub App"</strong></li>
                      </ol>
                    </div>

                    <div className="space-y-3">
                      <p className="text-sm font-medium">Step 2: Generate Private Key</p>
                      <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
                        <li>On your GitHub App page, scroll to <strong>"Private keys"</strong></li>
                        <li>Click <strong>"Generate a private key"</strong></li>
                        <li>A <code className="bg-muted px-1 rounded">.pem</code> file will download - save this securely</li>
                      </ol>
                    </div>

                    <div className="space-y-3">
                      <p className="text-sm font-medium">Step 3: Note Your App Details</p>
                      <p className="text-sm text-muted-foreground">
                        From your GitHub App's settings page, note:
                      </p>
                      <ul className="list-disc list-inside text-sm text-muted-foreground ml-2">
                        <li><strong>App ID</strong> (numeric, shown at top of page)</li>
                        <li><strong>App Slug</strong> (from the URL: github.com/apps/<code className="bg-muted px-1 rounded">your-app-slug</code>)</li>
                        <li><strong>Webhook secret</strong> (the one you set in Step 1)</li>
                        <li><strong>Private key</strong> (the .pem file contents)</li>
                      </ul>
                    </div>

                    <div className="space-y-3">
                      <p className="text-sm font-medium">Step 4: Store Credentials in AWS Secrets Manager</p>
                      <pre className="p-3 bg-slate-900 text-slate-100 rounded text-xs overflow-auto">
{`# Store GitHub App credentials in AWS Secrets Manager
aws secretsmanager create-secret \\
  --name kestrel/github-app-id \\
  --secret-string "123456" \\
  --region ${bedrockRegion || 'us-west-1'}

aws secretsmanager create-secret \\
  --name kestrel/github-app-webhook-secret \\
  --secret-string "your-webhook-secret" \\
  --region ${bedrockRegion || 'us-west-1'}

# For the private key, use --secret-binary or escape newlines:
aws secretsmanager create-secret \\
  --name kestrel/github-app-private-key \\
  --secret-string "$(cat your-app-private-key.pem)" \\
  --region ${bedrockRegion || 'us-west-1'}`}
                      </pre>
                    </div>

                    <div className="space-y-3">
                      <p className="text-sm font-medium">Step 5: Configure App Slug</p>
                      <p className="text-sm text-muted-foreground mb-2">
                        Set the GitHub App slug in your Helm values (add to your <code className="bg-muted px-1 rounded">kestrel-values.yaml</code>):
                      </p>
                      <pre className="p-3 bg-slate-900 text-slate-100 rounded text-xs overflow-auto">
{`# Add to kestrel-values.yaml under autonp-server:
autonp-server:
  github:
    app:
      slug: "your-app-slug"  # From github.com/apps/<slug>`}
                      </pre>
                      <p className="text-xs text-muted-foreground mt-2">
                        Then run: <code className="bg-muted px-1 rounded">helm upgrade kestrel kestrel-chart.tgz -f kestrel-values.yaml -n kestrel</code>
                      </p>
                    </div>

                    <div className="space-y-3">
                      <p className="text-sm font-medium">Step 6: Install the App</p>
                      <p className="text-sm text-muted-foreground">
                        After deployment, users can install your GitHub App on their repositories:
                      </p>
                      <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
                        <li>Go to <code className="bg-muted px-1 rounded">https://github.com/apps/your-app-slug</code></li>
                        <li>Click <strong>"Install"</strong> and select repositories</li>
                        <li>The integration will appear in Kestrel's CI/CD Integrations page</li>
                      </ol>
                    </div>

                    <Alert className="bg-amber-50 border-amber-200">
                      <AlertCircle className="h-4 w-4 text-amber-600" />
                      <AlertTitle className="text-amber-800">Requires Internet Access</AlertTitle>
                      <AlertDescription className="text-amber-700 text-sm">
                        GitHub integration requires outbound internet access to <code className="bg-amber-100 px-1 rounded">github.com</code> and <code className="bg-amber-100 px-1 rounded">api.github.com</code>. 
                        For air-gapped deployments, you will need to follow the onboarding instructions to integrate with GitHub Enterprise Server.
                      </AlertDescription>
                    </Alert>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        <Alert>
          <Shield className="h-4 w-4" />
          <AlertTitle>Mark as Deployed</AlertTitle>
          <AlertDescription>
            Once you've completed the deployment and DNS configuration, click below to update your deployment status.
          </AlertDescription>
        </Alert>

        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCurrentStep(5)}>
            Back
          </Button>
          <Button 
            onClick={async () => {
              try {
                if (status?.latest_release?.version) {
                  await apiClient.markOnPremDeployed({ version: status.latest_release.version });
                }
                handleStepComplete(6);
                setSuccess('Deployment marked as complete!');
                await loadData();
              } catch (err) {
                setError('Failed to update deployment status');
              }
            }}
            className="flex-1"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Mark as Deployed
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0: return renderOverviewStep();
      case 1: return renderDeploymentTypeStep();
      case 2: return renderLLMConfigStep();
      case 3: return renderInfrastructureStep();
      case 4: return renderGitHubIntegrationStep();
      case 5: return renderGenerateStep();
      case 6: return renderDownloadStep();
      default: return renderOverviewStep();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-4 bg-green-50 border-green-200">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700">{success}</AlertDescription>
        </Alert>
      )}

      {renderStepIndicator()}
      {renderCurrentStep()}

      {/* Credentials Modal */}
      <Dialog open={credentialsModalOpen} onOpenChange={setCredentialsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Registry Pull Credentials
            </DialogTitle>
            <DialogDescription>
              Use these credentials to pull private Kestrel on-prem images from our registry.
            </DialogDescription>
          </DialogHeader>
          
          {credentialsData && (
            <div className="space-y-4">
              {/* Credentials Info */}
              <div className="p-4 bg-muted rounded-lg space-y-2">
                <div className="grid grid-cols-[100px_1fr] gap-2 text-sm">
                  <span className="text-muted-foreground">Registry:</span>
                  <span className="font-mono">{credentialsData.registry}</span>
                </div>
                <div className="grid grid-cols-[100px_1fr] gap-2 text-sm">
                  <span className="text-muted-foreground">Username:</span>
                  <span className="font-mono">{credentialsData.username}</span>
                </div>
                <div className="grid grid-cols-[100px_1fr] gap-2 text-sm">
                  <span className="text-muted-foreground">Token:</span>
                  <span className="font-mono text-xs break-all">{credentialsData.token}</span>
                </div>
              </div>

              {/* Docker Login Command */}
              <div>
                <p className="text-sm font-medium mb-2">To authenticate Docker, run:</p>
                <pre className="p-3 bg-slate-900 text-slate-100 rounded text-xs overflow-auto">
                  {credentialsData.docker_login_command}
                </pre>
              </div>

              {/* Kubernetes Secret Command */}
              <div>
                <p className="text-sm font-medium mb-2">To create a Kubernetes pull secret:</p>
                <pre className="p-3 bg-slate-900 text-slate-100 rounded text-xs overflow-auto">
                  {credentialsData.k8s_secret_command}
                </pre>
              </div>

              <div className="text-xs text-muted-foreground">
                <p><strong>Note:</strong> Keep these credentials secure. Do not commit them to version control.</p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                if (credentialsData) {
                  navigator.clipboard.writeText(credentialsData.commands);
                }
              }}
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy Commands
            </Button>
            <Button onClick={() => setCredentialsModalOpen(false)}>
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnPremDeploymentPage;
